/**
 * AIDE CONTEXTUELLE KLAVOX — Regle IHM galaxie (CDC § « Une aide dans CHAQUE
 * vue, et c'est KOPILVIA »).
 *
 * Trois exigences du CDC, tenues ici :
 *  1. CONVERSATIONNELLE — l'utilisateur pose sa question dans ses mots
 *     (`askHelp`), il ne cherche pas dans un index.
 *  2. VERROU ANTI-CONFABULATION — une reponse ne peut PAS etre inventee : elle
 *     provient soit du catalogue ci-dessous (chaque entree cite un document du
 *     depot), soit de l'etat reel du compte lu sur cet appareil. Quand rien ne
 *     correspond, la reponse est « je ne sais pas, voici a qui demander » —
 *     c'est une reponse VALIDE et exigee, pas un echec.
 *  3. ELLE VOIT L'ETAT DU COMPTE — `accountFacts` transforme un instantane de
 *     l'application en faits citables (« aucun serveur de synchro configure »,
 *     « cle de recuperation non activee »).
 *
 * MODE DEGRADE ASSUME (CDC § « Hors ligne et on-premise ») : le hub Kopilvia
 * n'est pas provisionne pour Klavox (aucune cle API projet, cf. CDC § IA) ;
 * l'aide le DIT et sert ce qu'elle a en local. Elle ne fait jamais semblant.
 *
 * ZERO RESEAU, ZERO SECRET : ce module est pur. L'instantane ne porte que des
 * compteurs et des indicateurs — jamais un nom d'entree, jamais un mot de
 * passe, jamais la `vaultKey`. Ce que l'utilisateur tape ne quitte pas
 * l'appareil : il n'y a personne a qui l'envoyer.
 */

/** Les vues du produit. Chaque vue DOIT avoir au moins un sujet d'aide. */
export type HelpViewId =
  | 'auth-create'
  | 'auth-unlock'
  | 'auth-recover'
  | 'auth-recover-remote'
  | 'vault-list'
  | 'entry-detail'
  | 'entry-editor'
  | 'generator'
  | 'settings'
  | 'health'
  | 'send'
  | 'send-receive'
  | 'emergency-contact';

export interface HelpView {
  readonly id: HelpViewId;
  /** Titre tel qu'il apparait a l'ecran (« Vous etes sur : … »). */
  readonly label: string;
}

export const HELP_VIEWS: readonly HelpView[] = [
  { id: 'auth-create', label: 'Creer votre coffre' },
  { id: 'auth-unlock', label: 'Deverrouiller votre coffre' },
  { id: 'auth-recover', label: 'Reinitialiser via la cle de recuperation' },
  { id: 'auth-recover-remote', label: 'Recuperer votre compte' },
  { id: 'vault-list', label: 'Coffre' },
  { id: 'entry-detail', label: 'Fiche d’une entree' },
  { id: 'entry-editor', label: 'Editeur d’entree' },
  { id: 'generator', label: 'Generateur' },
  { id: 'settings', label: 'Parametres' },
  { id: 'health', label: 'Sante du coffre' },
  { id: 'send', label: 'Envoi ponctuel' },
  // Seule vue que quelqu'un peut atteindre SANS coffre : c'est aussi la
  // premiere page Klavox que verra un destinataire. Elle a donc son aide.
  { id: 'send-receive', label: 'Recevoir un envoi' },
  // Deuxieme vue atteignable SANS coffre : celle du contact de confiance, qui
  // n'a qu'un kit imprime et aucun compte Klavox (R32).
  { id: 'emergency-contact', label: 'Acces d’urgence' },
];

/**
 * Le moteur reellement en service. Klavox est classe « sans IA » (CDC) : aucun
 * appel au hub Kopilvia n'est cable, donc l'aide l'annonce au lieu de laisser
 * croire a une intelligence qu'elle n'a pas.
 */
export const HELP_ENGINE = {
  mode: 'local' as const,
  notice:
    'Aide locale. Le hub Kopilvia n’est pas configure sur cette installation : les reponses viennent de la ' +
    'documentation embarquee et de l’etat de votre coffre, lu sur cet appareil. Ce que vous tapez ici ne part nulle part.',
};

/** A qui s'adresser quand l'aide ne sait pas (reponse « je ne sais pas » du CDC). */
export const HELP_ESCALATION =
  'Posez la question a aide@klavox.santvia.eu, ou lisez `docs/ARCHITECTURE.md` et `README.md` dans le depot Klavox.';

export interface HelpTopic {
  readonly id: string;
  /** Vues ou le sujet est propose spontanement. */
  readonly views: readonly HelpViewId[];
  /** La question telle qu'un utilisateur la poserait (proposee en un clic). */
  readonly question: string;
  /** Mots declencheurs, sans accent ni majuscule (compares apres normalisation). */
  readonly keywords: readonly string[];
  readonly answer: string;
  /** Source citee : un fichier du depot. Une reponse sans source est un bug. */
  readonly source: string;
}

/**
 * Le catalogue. Il est la SEULE origine possible d'une reponse documentaire :
 * pas de generation, pas de paraphrase. Chaque `source` designe un fichier qui
 * existe dans le depot — verifie par `test/contextual-help.test.ts`.
 */
export const HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: 'urgence-principe',
    views: ['settings', 'emergency-contact'],
    question: 'Comment marche l’acces d’urgence, et que voit le serveur ?',
    keywords: ['urgence', 'heritage', 'heritier', 'deces', 'succession', 'contact de confiance', 'apres ma mort'],
    answer:
      'Vous designez un contact de confiance et vous lui remettez un KIT imprime, en main propre. Klavox scelle ' +
      'la cle de votre coffre sous ce kit : le serveur ne recoit que le resultat scelle, jamais le kit. Le ' +
      'serveur seul n’ouvre donc rien — c’est precisement la faiblesse qu’une etude de l’ETH Zurich a trouvee ' +
      'chez les concurrents, ou l’heritage passe par une cle publique que le serveur distribue et pourrait ' +
      'remplacer par la sienne.',
    source: 'docs/EMERGENCY-ACCESS-DESIGN.md',
  },
  {
    id: 'urgence-delai-refus',
    views: ['settings', 'emergency-contact'],
    question: 'Puis-je empecher un acces d’urgence que je n’ai pas voulu ?',
    keywords: ['refuser', 'refus', 'delai attente', 'annuler acces urgence', 'empecher heritier'],
    answer:
      'Oui. Quand votre contact demande l’acces, l’attente que vous avez choisie (24 heures a 90 jours) commence ' +
      'et vous en etes prevenu. « Refuser » annule la demande, meme si le delai etait deja ecoule. « Revoquer » ' +
      'va plus loin : c’est definitif, le serveur efface la copie scellee et le kit remis ne servira plus jamais. ' +
      'Limite dite franchement : delai et refus sont des regles appliquees par le serveur, pas des verrous ' +
      'mathematiques — sans le kit, personne n’ouvre rien, mais un serveur complice du contact pourrait ignorer ' +
      'l’attente.',
    source: 'docs/EMERGENCY-ACCESS-DESIGN.md',
  },
  {
    id: 'urgence-kit-perdu',
    views: ['settings', 'emergency-contact'],
    question: 'J’ai perdu le kit d’acces d’urgence, que faire ?',
    keywords: ['kit perdu', 'perdu le kit', 'reimprimer kit', 'retrouver kit urgence'],
    answer:
      'Il n’existe qu’en un exemplaire et Klavox ne le conserve pas : ni sur cet appareil, ni sur le serveur. ' +
      'Un kit perdu ne se reimprime pas. Revoquez la delegation concernee et designez de nouveau le contact : ' +
      'un kit neuf est alors genere. C’est le meme compromis que la cle de recuperation — ce que personne ne ' +
      'garde, personne ne peut se le faire voler.',
    source: 'apps/web/src/lib/emergency-access.ts',
  },
  {
    id: 'urgence-lecture-seule',
    views: ['emergency-contact'],
    question: 'Qu’est-ce que je peux faire avec le coffre que j’ouvre ?',
    keywords: ['lecture seule', 'modifier le coffre', 'contact de confiance droits', 'que puis-je faire'],
    answer:
      'Le lire, rien d’autre. Le serveur ne vous remet aucune session : il n’existe donc aucun moyen d’ecrire ' +
      'dans ce coffre, pas meme par erreur. Copiez ce dont vous avez besoin. Le titulaire, lui, peut couper ' +
      'l’acces a tout moment en revoquant la delegation.',
    source: 'apps/server/src/server.ts',
  },
  {
    id: 'send-principe',
    views: ['send', 'vault-list'],
    question: 'A quoi sert l’envoi ponctuel, et que voit le serveur ?',
    keywords: ['envoi', 'envoyer', 'partager', 'partage', 'send', 'transmettre', 'lien', 'donner un mot de passe'],
    answer:
      'Il transmet un secret a quelqu’un qui n’a pas de compte Klavox. Le contenu est chiffre sur cet appareil ' +
      'avec une cle tiree au hasard, et cette cle est placee APRES le « # » du lien : les navigateurs ne ' +
      'transmettent jamais cette partie de l’URL. Le serveur ne stocke donc que du chiffre, une date ' +
      'd’expiration et un compteur de telechargements — jamais de quoi ouvrir l’envoi.',
    source: 'packages/crypto-core/src/send.ts',
  },
  {
    id: 'send-fiche',
    views: ['send', 'entry-detail'],
    question: 'Comment partager une fiche entiere, et qu’est-ce qui n’en part jamais ?',
    keywords: ['partager une fiche', 'partager une entree', 'partager par lien', 'share item', 'partager un compte'],
    answer:
      '« Partager par lien », dans la fiche, en fait un envoi ponctuel : vous cochez les champs que le ' +
      'destinataire verra, et il les recoit un par un, les secrets masques jusqu’a ce qu’il les devoile ou les ' +
      'copie. Ne partent jamais, meme coches : la graine de double authentification (le lien donnerait un second ' +
      'facteur permanent, qui survivrait a son expiration), l’historique des mots de passe, les passkeys, la cle ' +
      'SSH privee et les pieces jointes. Une entree protegee par le mot de passe maitre doit etre deverrouillee avant.',
    source: 'apps/web/src/lib/entry-share.ts',
  },
  {
    id: 'send-mot-de-passe',
    views: ['send', 'send-receive'],
    question: 'Le mot de passe d’un envoi remplace-t-il le lien ?',
    keywords: ['mot de passe envoi', 'proteger un envoi', 'mot de passe du lien', 'second secret'],
    answer:
      'Non : il s’AJOUTE au lien, il ne le remplace pas. Les deux entrent dans la meme derivation de cle, ' +
      'donc le lien seul ne dechiffre rien, et le mot de passe seul non plus. Transmettez-les par deux canaux ' +
      'differents (le lien par message, le mot de passe de vive voix) : intercepter l’un ne suffit plus.',
    source: 'packages/crypto-core/src/send.ts',
  },
  {
    id: 'send-revocation',
    views: ['send'],
    question: 'Puis-je annuler un envoi deja parti ?',
    keywords: ['revoquer', 'revocation', 'annuler un envoi', 'supprimer un lien', 'retirer'],
    answer:
      'Oui : « Revoquer » rend le lien inoperant immediatement, pour tout le monde. L’expiration et le nombre ' +
      'de telechargements font la meme chose automatiquement. En revanche, ce qu’un destinataire a DEJA lu, ' +
      'il l’a lu : aucun coffre ne sait effacer une memoire ni un copier-coller.',
    source: 'apps/web/src/lib/send-client.ts',
  },
  {
    id: 'send-modification',
    views: ['send'],
    question: 'Puis-je prolonger un envoi ou changer son nombre de telechargements ?',
    keywords: ['modifier un envoi', 'prolonger', 'prolonger un lien', 'changer l’expiration', 'plus de telechargements', 'note privee'],
    answer:
      'Oui : « Modifier » sur la ligne de l’envoi change son echeance (30 jours au plus a compter d’aujourd’hui), ' +
      'son nombre de telechargements et votre note privee. Le lien reste le meme : rien a renvoyer au destinataire. ' +
      'Un envoi epuise peut ainsi etre rouvert. Le contenu et le mot de passe, eux, ne se modifient pas — refaites ' +
      'un envoi. Et un envoi revoque ne se rallume jamais.',
    source: 'apps/web/src/lib/send-client.ts',
  },
  {
    id: 'send-reception',
    views: ['send-receive'],
    question: 'Ai-je besoin d’un compte pour ouvrir cet envoi ?',
    keywords: ['recevoir', 'ouvrir un envoi', 'compte necessaire', 'destinataire', 'pas de compte'],
    answer:
      'Non. Le lien que vous avez recu contient tout ce qu’il faut, et le dechiffrement se fait dans ce ' +
      'navigateur. Attention : le telechargement peut etre limite en nombre et dans le temps — une fois ' +
      'l’envoi epuise ou expire, il n’existe plus, et son emetteur devra le prolonger ou en refaire un.',
    source: 'apps/web/src/lib/send-client.ts',
  },
  {
    id: 'mot-de-passe-maitre',
    views: ['auth-create', 'auth-unlock', 'settings'],
    question: 'Qu’est-ce que le mot de passe maitre, et qui peut le lire ?',
    keywords: ['mot de passe maitre', 'master', 'maitre', 'qui peut lire', 'serveur voit'],
    answer:
      'C’est l’unique secret qui ouvre votre coffre. Il n’est ni stocke, ni transmis, ni journalise : il sert ' +
      'a deriver une cle (Argon2id) sur cet appareil, et cette cle seule dechiffre vos entrees. Le serveur ne ' +
      'recoit que du chiffre — il ne peut pas le retrouver, meme sous contrainte.',
    source: 'docs/ARCHITECTURE.md',
  },
  {
    id: 'mot-de-passe-maitre-solide',
    views: ['auth-create', 'auth-recover', 'auth-recover-remote', 'settings'],
    question: 'Comment choisir un mot de passe maitre solide ?',
    keywords: ['solide', 'force', 'robuste', 'faible', 'choisir', 'phrase de passe', 'mot de passe maitre faible', 'fuite', 'expose'],
    answer:
      'Sa force s’affiche pendant la frappe, mesuree sur cet appareil comme la Sante du coffre : mots courants, ' +
      'prenoms, dates, suites, motifs de clavier, et aussi votre e-mail et le mot « Klavox », que tout attaquant ' +
      'visant votre coffre essaiera en premier. Le meilleur choix : une phrase de passe de quatre mots ou plus, ' +
      'sans lien avec vous. Un mot de passe faible n’est accepte que si vous cochez « Utiliser ce mot de passe ' +
      'faible quand meme » : il protege tout le coffre, et sa copie chiffree se devine hors ligne. Cochez aussi ' +
      '« Verifier ce mot de passe dans les fuites connues » : un mot de passe deja fuite est essaye en premier, ' +
      'meme long. Seuls les 5 premiers caracteres de son empreinte SHA-1 partent (k-anonymat), jamais le mot de passe.',
    source: 'apps/web/src/lib/master-password-gate.ts',
  },
  {
    id: 'mot-de-passe-expose-fiche',
    views: ['entry-editor'],
    question: 'Comment savoir si le mot de passe que je saisis a deja fuite ?',
    keywords: ['fuite', 'fuites connues', 'expose', 'pwned', 'compromis', 'verifier', 'bouclier', 'k-anonymat'],
    answer:
      'Le bouton bouclier a cote du mot de passe de la fiche le cherche dans les fuites connues, avant meme ' +
      'l’enregistrement. Rien ne part sans ce clic, et seuls les 5 premiers caracteres de son empreinte SHA-1 ' +
      'quittent l’appareil (k-anonymat), jamais le mot de passe. Expose, il est essaye en premier par les ' +
      'attaquants, meme long : generez-en un autre. Absent, il n’est pas robuste pour autant : la jauge en juge. ' +
      'Le verdict se masque des que vous modifiez le mot de passe. Le service interroge est notre miroir europeen ' +
      '(fuites.santvia.eu), ou celui des Parametres ; aucun service tiers.',
    source: 'apps/web/src/lib/draft-breach-check.ts',
  },
  {
    id: 'mot-de-passe-maitre-perdu',
    views: ['auth-unlock', 'auth-recover', 'settings'],
    question: 'J’ai oublie mon mot de passe maitre, que faire ?',
    keywords: [
      'oublie',
      'oublie mon mot de passe',
      'oublie mon mot de passe maitre',
      'perdu mon mot de passe',
      'perdu',
      'perdre',
      'retrouver mon mot de passe maitre',
      'reinitialiser',
    ],
    answer:
      'Sans mot de passe maitre, seule la cle de recuperation rouvre le coffre : elle en detient une copie ' +
      'scellee. Si elle n’a jamais ete activee, personne — pas meme SANTVIA — ne peut dechiffrer vos donnees ; ' +
      'c’est la contrepartie du zero-knowledge.',
    source: 'docs/RECOVERY-KEY-DESIGN.md',
  },
  {
    id: 'cle-de-recuperation',
    views: ['auth-recover', 'auth-recover-remote', 'settings'],
    question: 'Comment fonctionne la cle de recuperation ?',
    keywords: ['cle de recuperation', 'recuperation', 'recuperer', 'code de secours', 'imprimer'],
    answer:
      'A son activation, une cle aleatoire est generee sur l’appareil et scelle une seconde copie de la cle du ' +
      'coffre. Elle s’affiche UNE SEULE FOIS : imprimez-la ou rangez-la hors ligne. La saisir permet de ' +
      'redefinir un mot de passe maitre sans rien perdre du contenu.',
    source: 'docs/RECOVERY-KEY-DESIGN.md',
  },
  {
    id: 'nouvel-appareil',
    views: ['auth-create', 'auth-recover-remote'],
    question: 'Comment ouvrir mon coffre sur un nouvel appareil ?',
    keywords: ['nouvel appareil', 'autre appareil', 'nouveau telephone', 'changer ordinateur', 'migrer'],
    answer:
      'Si le coffre est synchronise, indiquez l’URL du serveur et votre e-mail : le coffre chiffre est ' +
      'telecharge, puis ouvert localement par votre mot de passe maitre (ou par la cle de recuperation, qui en ' +
      'definit un nouveau). Un coffre purement local, lui, ne quitte jamais son appareil : passez par l’export ' +
      'chiffre des parametres.',
    source: 'docs/ARCHITECTURE.md',
  },
  {
    id: 'coffre-local-sans-compte',
    views: ['auth-create', 'auth-unlock', 'settings'],
    question: 'Suis-je oblige de creer un compte en ligne ?',
    keywords: ['compte', 'en ligne', 'inscription', 'gratuit', 'hors ligne', 'sans internet'],
    answer:
      'Non. Le coffre fonctionne entierement hors ligne sur cet appareil ; l’e-mail ne sert alors que ' +
      'd’identifiant de derivation, il n’est envoye nulle part. Un serveur n’est necessaire que pour ' +
      'synchroniser plusieurs appareils.',
    source: 'README.md',
  },
  {
    id: 'ce-que-voit-le-serveur',
    views: ['settings', 'auth-recover-remote', 'vault-list'],
    question: 'Qu’est-ce que le serveur voit exactement ?',
    keywords: ['serveur', 'voit', 'santvia', 'confiance', 'heberge', 'donnees', 'metadonnees'],
    answer:
      'Des blobs chiffres, leurs identifiants, leurs numeros de revision et leurs dates. Jamais un nom ' +
      'd’entree, une URL, un identifiant ou un mot de passe : le chiffrement et le dechiffrement se font ' +
      'uniquement sur vos appareils. Le detail exhaustif de ce qui transite est ecrit et verifie par des tests.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'synchronisation',
    views: ['settings', 'vault-list'],
    question: 'Comment synchroniser mes appareils ?',
    keywords: ['synchro', 'synchroniser', 'sync', 'plusieurs appareils', 'serveur klavox'],
    answer:
      'Dans Parametres, indiquez l’URL de votre serveur (klavox.santvia.eu ou votre propre instance) et connectez-vous. ' +
      'Seuls des blobs chiffres partent ; les conflits sont resolus par revision, sans jamais ouvrir le contenu ' +
      'cote serveur.',
    source: 'docs/ARCHITECTURE.md',
  },
  {
    id: 'auto-heberger',
    views: ['settings', 'auth-recover-remote'],
    question: 'Puis-je heberger le serveur moi-meme ?',
    keywords: ['auto-heberge', 'heberger', 'self-host', 'mon serveur', 'instance'],
    answer:
      'Oui : `apps/server` est un backend souverain (stockage du chiffre + synchro + authentification) qui ne ' +
      'voit rien en clair. Vous pointez l’application vers votre URL ; aucun composant de Klavox n’exige le ' +
      'serveur de SANTVIA.',
    source: 'apps/server/README.md',
  },
  {
    id: 'verrouillage-auto',
    views: ['vault-list', 'settings', 'entry-detail'],
    question: 'Quand le coffre se verrouille-t-il tout seul ?',
    keywords: ['verrouille', 'verrouillage', 'inactivite', 'timeout', 'se ferme'],
    answer:
      'Apres cinq minutes sans interaction par defaut, et a chaque fois que vous cliquez sur Verrouiller. Le delai ' +
      'se regle dans Parametres > « Verrouillage et presse-papiers », de 1 minute a 4 heures, et vous pouvez ' +
      'aussi verrouiller des que vous quittez l’onglet. Le verrouillage efface de la memoire la cle du coffre, les ' +
      'secrets dechiffres et les pieces jointes : il faut le mot de passe maitre pour rouvrir.',
    source: 'apps/web/src/lib/lock-settings.ts',
  },
  {
    id: 'taille-du-texte',
    views: ['settings', 'auth-unlock', 'vault-list'],
    question: 'Comment agrandir le texte de Klavox ?',
    keywords: ['agrandir', 'zoom', 'police', 'lisible', 'lire', 'taille du texte', 'trop petit'],
    answer:
      'Parametres, rubrique Affichage : « Taille du texte » va de 80 % a 200 %. Le choix s’applique aussitot a ' +
      'tout Klavox, ecran de deverrouillage compris, et reste retenu sur cet appareil. Ce ne sont que des ' +
      'preferences d’affichage : rien du coffre n’y est ecrit.',
    source: 'apps/web/src/ui/socle.ts',
  },
  {
    id: 'alias-e-mail',
    views: ['generator', 'entry-editor'],
    question: 'A quoi sert un alias e-mail, et ou part ma cle d’API ?',
    keywords: ['alias', 'e-mail', 'email', 'adresse', 'addy', 'simplelogin', 'spam'],
    answer:
      'Un alias est une adresse jetable creee chez VOTRE fournisseur (addy.io, SimpleLogin) : le site ne connait ' +
      'jamais votre vraie adresse, et un alias qui recoit du spam designe le site qui a fuite. La demande part de ' +
      'cet appareil vers le fournisseur : le serveur Klavox ne voit ni votre cle d’API, ni l’alias. La cle reste en ' +
      'memoire le temps de la session — enregistrez-la dans une entree du coffre pour la retrouver.',
    source: 'docs/ARCHITECTURE.md',
  },
  {
    id: 'presse-papiers',
    views: ['entry-detail', 'vault-list', 'generator'],
    question: 'Combien de temps le mot de passe reste-t-il dans le presse-papiers ?',
    keywords: ['presse-papiers', 'copier', 'copie', 'coller', 'clipboard'],
    answer:
      'Trente secondes par defaut : Klavox efface le presse-papiers automatiquement apres ce delai, compte depuis ' +
      'la derniere copie. Il se regle dans Parametres > « Verrouillage et presse-papiers », de 10 secondes a 5 ' +
      'minutes. Une autre copie faite entre-temps remplace la precedente, comme partout ailleurs sur le systeme.',
    source: 'apps/web/src/lib/lock-settings.ts',
  },
  {
    id: 'types-d-entree',
    views: ['entry-editor', 'vault-list'],
    question: 'Que puis-je ranger dans le coffre, en dehors des mots de passe ?',
    keywords: ['type', 'carte', 'bancaire', 'identite', 'note', 'ranger', 'stocker'],
    answer:
      'Identifiants, cartes bancaires, pieces d’identite et notes securisees, avec leurs champs types (numero, ' +
      'expiration, cryptogramme…). Tous les champs sont chiffres de la meme facon : il n’y a pas de champ ' +
      '« moins sensible » stocke en clair.',
    source: 'packages/vault-model/README.md',
  },
  {
    id: 'domaines-equivalents',
    views: ['entry-editor'],
    question: 'Que change la case « Reconnaitre aussi sur les domaines equivalents » ?',
    keywords: ['domaines equivalents', 'equivalent', 'meme compte', 'autre domaine', 'youtube', 'reconnaitre', 'safari', 'nouveau domaine'],
    answer:
      'Certains services authentifient sur plusieurs domaines (youtube.com et google.com, apple.com et ' +
      'icloud.com…). Cochee, l’entree est reconnue sur tous les domaines de son groupe ; decochee, uniquement sur ' +
      'son propre site. Les groupes viennent d’une liste sourcee et versionnee, jamais d’une deduction, et ne ' +
      'rapprochent jamais deux sites sur un suffixe public : la liste de Bitwarden et celle d’Apple que suivent ' +
      'Safari et le trousseau iCloud (embarquee, jamais interrogee en ligne). Certains liens n’ont qu’un sens : ' +
      'l’identifiant d’un domaine abandonne (hbo.com) est reconnu sur le nouveau (max.com), jamais l’inverse ; un ' +
      'portail de connexion (signin.costco.com) ne vaut que pour lui-meme, pas pour tout son site. Le choix est ' +
      'chiffre avec l’entree.',
    source: 'docs/RECONNAISSANCE-SITES.md',
  },
  {
    id: 'adresses-entree',
    views: ['entry-editor', 'entry-detail'],
    question: 'Une entree peut-elle porter plusieurs adresses ?',
    keywords: ['plusieurs adresses', 'adresse', 'url', 'site', 'autre site', 'ajouter une adresse', 'ouvrir', 'ordre', 'reordonner', 'site principal'],
    answer:
      'Oui. « Ajouter une adresse » sous le site en ajoute une autre (page de connexion sur un domaine distinct, ' +
      'ancien nom du service) ; la poubelle de la ligne la retire a l’enregistrement. L’entree est reconnue sur ' +
      'chacune de ses adresses, et la fiche les montre toutes : chacune se copie, et une adresse web (http ou ' +
      'https) s’ouvre dans un nouvel onglet ; une adresse d’application ou d’un autre schema reste du texte. Les ' +
      'adresses importees ou reunies par une fusion de doublons apparaissent ici. La premiere donne le domaine ' +
      'affiche dans la liste. Les fleches de chaque ligne changent l’ordre : monter la deuxieme adresse en fait le ' +
      'site principal, et chaque adresse garde sa detection en changeant de ligne.',
    source: 'apps/web/src/lib/entry-uris.ts',
  },
  {
    id: 'raccourcis-clavier',
    views: ['vault-list', 'entry-detail'],
    question: 'Quels sont les raccourcis clavier ?',
    keywords: ['raccourci', 'raccourcis', 'clavier', 'touche', 'sans souris', 'shortcut', 'ctrl+c', 'copier vite', 'changer une touche', 'desactiver les raccourcis', 'dictee vocale'],
    answer:
      '« / » place le curseur dans la recherche ; une fois dedans, les fleches bas et haut ouvrent l’entree ' +
      'suivante ou precedente sans quitter le champ, et Echap l’efface. Hors d’un champ de saisie : j et k pour ' +
      'l’entree suivante ou precedente, n pour une nouvelle entree, e pour modifier celle qui est ouverte, u, p et t ' +
      'pour copier son identifiant, son mot de passe ou son code 2FA, g pour l’afficher en grand format, l pour verrouiller, ? pour revoir cette liste. ' +
      'Ctrl+C (Cmd+C sur Mac) copie le mot de passe de l’entree ouverte quand aucun texte n’est selectionne, ' +
      'Ctrl+B son identifiant. Dans l’arbre des dossiers comme dans celui des etiquettes : fleches d’une ligne a l’autre, ' +
      'Droite et Gauche deplient ou replient, * deplie les voisins, et taper le debut d’un nom y mene (sans casse ni ' +
      'accents) -- la, les lettres cherchent un nom et ne declenchent aucun raccourci. Une entree qui redemande le mot de passe maitre ne livre rien avant confirmation, ' +
      'et une lettre tapee dans un champ reste une lettre. Ces lettres sont celles par defaut : Parametres › ' +
      'Raccourcis clavier montre celles de cet appareil, en change chacune, ou coupe tous les raccourcis d’une ' +
      'seule touche (dictee vocale, frappe accidentelle) -- Ctrl+C et Ctrl+B restent actifs.',
    source: 'apps/web/src/lib/keyboard-shortcuts.ts',
  },
  {
    id: 'grand-format',
    views: ['entry-detail'],
    question: 'Comment recopier un mot de passe a la main sur un autre appareil ?',
    keywords: ['grand format', 'recopier', 'dicter', 'television', 'console', 'caractere par caractere', 'large type', 'agrandir', 'ancien mot de passe'],
    answer:
      'Sur la fiche, le bouton « Grand format » a cote du mot de passe le montre en tres gros, un caractere par ' +
      'case, numerote. Il existe aussi a cote du numero de carte, du CVV, d’un PIN, d’un IBAN, de la phrase de ' +
      'passe d’un portefeuille, de chaque champ personnalise, de chaque ancien mot de passe de l’historique et de ' +
      'ces memes champs dans une version precedente depliee ; le titre nomme le champ montre (et la date de la ' +
      'version). Au clavier, g ouvre celui du mot de passe de l’entree ouverte. ' +
      'Les cases : majuscules, minuscules, chiffres et symboles ont chacun leur couleur, et chaque case dit son ' +
      'nom au survol et au lecteur d’ecran (« minuscule l », « chiffre 1 », « tiret »). Un caractere invisible ' +
      'laisse par un copier-coller (espace insecable, espace de largeur nulle) est montre par son code, en rouge. ' +
      'Echap, la croix ou un clic a cote le ferme ; il se ferme aussi en changeant d’entree et au verrouillage. ' +
      'Une entree qui redemande le mot de passe maitre ne s’affiche pas avant confirmation.',
    source: 'apps/web/src/lib/large-type.ts',
  },
  {
    id: 'notes-mise-en-forme',
    views: ['entry-editor', 'entry-detail'],
    question: 'Comment mettre en forme les notes d’une entree ?',
    keywords: ['notes', 'markdown', 'gras', 'italique', 'titre', 'liste', 'case a cocher', 'lien', 'code', 'tableau', 'colonnes', 'texte brut', 'mise en forme', 'apercu'],
    answer:
      'Les notes acceptent la mise en forme Markdown : **gras**, *italique*, ~~barre~~, `code`, # titre, ' +
      '- liste (decaler de deux espaces pour une sous-liste), 1. liste numerotee, - [ ] case a cocher, > citation, ' +
      '--- pour un filet, [texte](https://adresse) pour un lien et un bloc de code entre deux lignes ```. ' +
      'Un tableau s’ecrit en colonnes separees par | : une ligne d’en-tete, puis | --- | --- | dessous ' +
      '(:---: centre la colonne, ---: l’aligne a droite) ; \\| met un | dans une cellule. ' +
      'Une adresse web ecrite telle quelle devient un lien. Un lien ne s’ouvre que vers une adresse http ou https, ' +
      'dans un nouvel onglet, sans dire au site d’ou l’on vient ; une image n’est jamais chargee (elle devient un ' +
      'lien), et du HTML tape dans une note s’affiche comme du texte. Une emphase ne s’applique jamais au milieu ' +
      'd’un mot : un code comme x*y*z ou ab_cd_ef reste intact. Le bouton « Texte brut » de la fiche montre la ' +
      'note telle qu’elle a ete tapee ; ce choix est garde sur cet appareil. Dans l’editeur, le bouton « Apercu » ' +
      'montre sous la zone de saisie la note telle que la fiche l’affichera, mise a jour a chaque frappe.',
    source: 'apps/web/src/lib/notes-markdown.ts',
  },
  {
    id: 'detection-adresse',
    views: ['entry-editor', 'entry-detail', 'settings'],
    question: 'Comment choisir sur quelles pages une adresse est reconnue ?',
    keywords: ['detection', 'correspondance', 'hote', 'port', 'commence par', 'adresse exacte', 'expression reguliere', 'jamais', 'uri match', 'par defaut', 'reglage'],
    answer:
      'Chaque adresse a son menu « Detection ». « Par defaut » suit le reglage du coffre (Parametres, « Detection ' +
      'des sites » : Domaine, Hote et port, Commence par ou Adresse exacte), retenu sur cet appareil. « Domaine » reconnait le site et ses sous-domaines ' +
      '(et ses domaines equivalents si la case est cochee). « Hote et port » isole une application parmi d’autres ' +
      'sur un meme domaine (intranet.exemple.fr:8443) ; « Commence par » et « Adresse exacte » visent une page ' +
      'precise, sans jamais reconnaitre un domaine qui ne fait que commencer pareil ; « Expression reguliere » est ' +
      'reservee aux usages avances ; « Jamais » garde l’adresse sans la proposer. La fiche signale toute regle ' +
      'autre que le domaine, et un « Domaine » choisi quand le defaut du coffre est plus strict. Le choix est chiffre ' +
      'avec l’entree, et un import Bitwarden garde celui de chaque adresse.',
    source: 'packages/vault-model/src/uri-match.ts',
  },
  {
    id: 'champs-personnalises',
    views: ['entry-editor', 'entry-detail'],
    question: 'A quoi servent les champs personnalises d’une entree ?',
    keywords: ['champ personnalise', 'champs personnalises', 'ajouter un champ', 'pin', 'numero client', 'question secrete', 'masque', 'plusieurs lignes', 'multiligne', 'case a cocher'],
    answer:
      'Ils rangent ce que les champs standards ne prevoient pas : numero client, PIN, question secrete… « Ajouter ' +
      'un champ » dans la fiche, puis un nom et une valeur ; cochez « Masque » pour qu’elle reste cachee a l’ecran ' +
      'jusqu’a l’oeil. Une valeur peut tenir sur plusieurs lignes (cle, certificat, codes de secours) : Maj+Entree ' +
      'passe a la ligne, un texte colle garde les siennes, et la fiche les affiche telles quelles. ' +
      '« Ajouter une case a cocher » range un oui / non (« Compte professionnel ») : la fiche montre la case, cochee ou non. ' +
      'Le nom comme la valeur sont chiffres avec l’entree, masque ou non : le serveur ne voit ni ' +
      'l’un ni l’autre. Les imports Bitwarden, KeePass, CXF, 1Password et Proton Pass les reprennent tels quels, masquage compris (et les cases a cocher de Bitwarden, de Keeper et du CXF) ; ' +
      'd’un CSV, chaque colonne inconnue devient un champ, masque si son nom designe un secret (PIN, mot de passe, IBAN…).',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'redemander-mot-de-passe-maitre',
    views: ['entry-editor', 'entry-detail'],
    question: 'Comment proteger une entree sensible meme quand le coffre est ouvert ?',
    keywords: ['redemander', 'reprompt', 'entree sensible', 'entree protegee', 'retaper', 'mot de passe maitre redemande'],
    answer:
      'Cochez « Redemander le mot de passe maitre » dans la fiche de l’entree. Elle n’affiche alors que son nom et son ' +
      'site : mot de passe, code, champs et notes restent hors de l’ecran — et hors du presse-papiers — tant que vous ' +
      'n’avez pas retape votre mot de passe maitre. La verification se fait sur cet appareil, rien n’est transmis, et ' +
      'elle est redemandee des que vous changez d’entree ou verrouillez le coffre. Le choix est chiffre avec l’entree ; ' +
      'les exports Bitwarden (JSON et CSV) qui le portent le gardent a l’import.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'archiver-une-entree',
    views: ['vault-list', 'entry-detail'],
    question: 'Comment ranger un ancien compte sans le supprimer ?',
    keywords: ['archiver', 'archive', 'archives', 'desarchiver', 'ancien compte', 'cacher une entree'],
    answer:
      'Ouvrez l’entree et choisissez « Archiver ». Elle quitte Tous les elements, les favoris, les dossiers et la ' +
      'recherche, sans etre supprimee : la vue « Archives » la garde, et « Desarchiver » la remet en place. Elle reste ' +
      'synchronisee, exportee et analysee par la Sante du coffre, car son mot de passe peut encore ouvrir un compte. ' +
      'Le choix est chiffre avec l’entree : le serveur ne sait pas ce qui est archive. Les elements archives de ' +
      '1Password et de Bitwarden restent aux archives a l’import.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'lier-des-elements',
    views: ['entry-detail'],
    question: 'Comment relier une entree a une autre (messagerie, carte, cle SSH) ?',
    keywords: ['lier', 'lie', 'lien', 'relier', 'element lie', 'elements lies', 'delier', 'related'],
    answer:
      'Ouvrez l’entree et choisissez « Lier un element », puis l’entree qui va avec : la messagerie qui recoit les ' +
      'reinitialisations, la carte enregistree chez le marchand, la cle SSH du serveur. Le lien se voit dans les deux ' +
      'fiches et ouvre l’autre en un clic, meme archivee ou rangee dans un autre dossier ; la croix le retire sans rien ' +
      'supprimer. Le lien est chiffre avec l’entree : le serveur ne sait pas quelles entrees vont ensemble.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'trier-la-liste',
    views: ['vault-list'],
    question: 'Comment trier la liste des entrees ?',
    keywords: ['trier', 'tri', 'ordre', 'classer', 'recent', 'recentes', 'modifiees', 'creees', 'alphabetique'],
    answer:
      'Au-dessus de la liste, « Trier » propose : Nom (A → Z), Nom (Z → A), Modifiees recemment, Creees recemment, ' +
      'Plus anciennes d’abord. L’ordre vaut pour toutes les vues, recherche, archives et corbeille comprises, et cet ' +
      'appareil s’en souvient. Il ne revele rien de plus au serveur : les dates de creation et de modification sont ' +
      'des horodatages qu’il voit deja pour la synchronisation, le nom reste chiffre.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'selection-multiple',
    views: ['vault-list'],
    question: 'Comment deplacer, archiver ou supprimer plusieurs entrees a la fois ?',
    keywords: ['plusieurs', 'selectionner', 'selection', 'cocher', 'lot', 'en masse', 'toutes', 'groupe', 'deplacer', 'maj', 'shift', 'plage', 'fleche', 'espace', 'origine', 'ctrl+a'],
    answer:
      'Au-dessus de la liste, « Selectionner » fait apparaitre une case devant chaque entree. Cochez-en plusieurs, ou ' +
      '« Tout cocher » ; Maj+clic coche (ou decoche) d’un coup toutes les entrees affichees entre la derniere ' +
      'cochee et celle-ci. Au clavier : fleches bas / haut d’une entree a l’autre, Origine / Fin a la premiere / ' +
      'derniere, Espace coche, Maj+fleche (ou Maj+Origine / Maj+Fin) etend la plage, Maj+Espace fait comme Maj+clic, ' +
      'Ctrl+A (Cmd+A sur Mac) coche tout ce qui est affiche. Puis choisissez : ajouter ou retirer des favoris, deplacer vers un dossier, archiver, ' +
      'supprimer (corbeille, restaurable) ; dans la Corbeille : restaurer ou supprimer definitivement. Seules les ' +
      'entrees cochees ET affichees sont touchees : une recherche qui en masque une la protege. Si une entree ' +
      'sensible est cochee, le mot de passe maitre est redemande, comme pour sa fiche.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'exclure-de-la-sante',
    views: ['health', 'entry-detail'],
    question: 'Comment ne plus voir d’alerte pour une entree que je garde sciemment ?',
    keywords: ['exclure', 'exclue', 'ignorer', 'ignorer une alerte', 'alerte', 'surveiller', 'surveillance', 'faux positif', 'compte de test'],
    answer:
      'Ouvrez l’entree et choisissez « Exclure de la Sante ». Elle ne leve plus d’alerte et ne compte plus dans le ' +
      'score, mais elle n’est jamais cachee : la Sante du coffre la liste a part, sous « Exclues de l’analyse », avec ' +
      '« Surveiller a nouveau ». Son mot de passe compte toujours : une autre entree qui le reutilise reste signalee. ' +
      'Une copie de l’entree nait surveillee. Pour ne faire taire qu’UNE alerte (faible, reutilise, ancien, non chiffre, ' +
      '2FA disponible), la croix de son badge dans la Sante du coffre l’ignore pour cette entree seulement : le reste ' +
      'est toujours surveille, et l’alerte reste listee sous « Alertes ignorees » avec « Signaler » pour la retablir. ' +
      'Une fuite, un site compromis ou une adresse sosie ne s’ignorent jamais un a un. ' +
      'Le choix est chiffre avec l’entree : le serveur ne sait pas ce qui est exclu ni ignore.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'dupliquer-une-entree',
    views: ['entry-detail'],
    question: 'Comment dupliquer une entree, pour un second compte sur le meme site ?',
    keywords: ['dupliquer', 'copie', 'cloner', 'clone', 'second compte', 'meme site'],
    answer:
      'Ouvrez l’entree et choisissez « Dupliquer ». La copie, nommee « (copie) », reprend identifiant, mot de passe, ' +
      'code a usage unique, sites, notes et champs, dans le meme dossier, et s’ouvre aussitot en modification. Elle ' +
      'laisse a l’original ses passkeys (une passkey n’ouvre qu’un seul compte), l’historique de ses mots de passe ' +
      'et ses pieces jointes. Une entree sensible donne une copie sensible : la re-demande du mot de passe maitre ' +
      'est toujours conservee.',
    source: 'docs/SERVER_VISIBILITY.md',
  },
  {
    id: 'totp',
    views: ['entry-editor', 'entry-detail', 'health'],
    question: 'A quoi sert le champ code a usage unique (TOTP) ?',
    keywords: ['totp', 'code a usage unique', 'double facteur', '2fa', 'authenticator', 'otp', 'code qr', 'qr', 'capture', 'scanner', 'steam', 'steam guard', 'transferer', 'migrer', 'autre appareil'],
    answer:
      'Il stocke la graine fournie par un site lors de l’activation du second facteur : Klavox affiche alors le ' +
      'code a six chiffres et son compte a rebours, a cote de l’identifiant (un code Steam Guard a cinq caracteres ' +
      'pour une graine steam://, comme Bitwarden la note). La graine est chiffree comme le ' +
      'reste du coffre. Le site montre un code QR : faites-en une capture d’ecran, puis « Lire un code QR » sous ' +
      'le champ, ou collez-la (Ctrl+V) dans le champ, ou deposez-la dessus. L’image est lue sur cet appareil, ' +
      'jamais envoyee ni gardee ; la graine est reprise entiere (duree, nombre de chiffres), et un nom ou un ' +
      'identifiant vides prennent le site et le compte du code. Dans l’autre sens, le bouton code QR a cote du ' +
      'code affiche la graine en code QR, a scanner avec une application d’authentification (un deuxieme ' +
      'appareil, une migration) : elle doit montrer le meme code que Klavox. Steam Guard n’y est pas propose.',
    source: 'apps/web/src/lib/qr-decode.ts',
  },
  {
    id: 'pieces-jointes',
    views: ['entry-detail'],
    question: 'Les pieces jointes sont-elles protegees ?',
    keywords: ['piece jointe', 'fichier', 'joindre', 'document', 'attacher'],
    answer:
      'Oui : chaque fichier est chiffre sur l’appareil avant tout stockage ou toute synchro, son nom compris, ' +
      'et lie a son entree par les donnees authentifiees. Le serveur n’en voit que la taille.',
    source: 'docs/DATA-CLASSIFICATION.md',
  },
  {
    id: 'apercu-piece-jointe',
    views: ['entry-detail'],
    question: 'Puis-je voir une piece jointe sans la telecharger ?',
    keywords: ['apercu', 'voir piece jointe', 'ouvrir piece jointe', 'afficher fichier', 'quick look', 'previsualiser'],
    answer:
      'Oui, l’oeil a cote du fichier : les images (PNG, JPEG, GIF, WebP) et les textes s’affichent dans le coffre, ' +
      'dechiffres en memoire. Rien n’est ecrit sur le disque, contrairement a Telecharger qui laisse une copie en ' +
      'clair dans vos telechargements ; l’apercu est oublie a la fermeture, en changeant d’entree et au verrouillage. ' +
      'Le type est lu au contenu, jamais au nom ; un SVG, un PDF ou une archive ne s’affichent pas et se telechargent.',
    source: 'apps/web/src/lib/attachment-preview.ts',
  },
  {
    id: 'historique',
    views: ['entry-detail'],
    question: 'Puis-je retrouver un ancien mot de passe remplace ?',
    keywords: ['historique', 'ancien mot de passe', 'restaurer', 'precedent', 'annuler'],
    answer:
      'Oui : chaque remplacement conserve l’ancienne valeur dans un historique chiffre, masque par defaut et ' +
      'restaurable depuis la fiche. Vous pouvez aussi le purger d’un clic.',
    source: 'docs/DATA_PROTECTION.md',
  },
  {
    id: 'versions-entree',
    views: ['entry-detail'],
    question: 'Puis-je revenir a une version precedente d’une entree ?',
    keywords: ['version', 'versions precedentes', 'voir une ancienne version', 'comparer', 'comparer a aujourd hui', 'difference', 'mot a mot', 'mots ajoutes', 'mots retires', 'avant apres', 'revenir en arriere', 'annuler une modification', 'ecrase', 'note effacee', 'restaurer une entree', 'historique de l entree'],
    answer:
      'Oui : chaque enregistrement qui change le contenu d’une entree (nom, identifiant, mot de passe, sites, notes, ' +
      'champs, carte, identite...) garde la version remplacee, chiffree avec l’entree ; les 10 plus recentes sont ' +
      'conservees. La section « Versions precedentes » de la fiche dit quand chaque version a ete remplacee et quels ' +
      'champs ont change depuis. La fleche d’une version en deplie le contenu complet, champ par champ, un filet or ' +
      'marquant ce qui a change depuis, les mots que la modification suivante a retires barres dans la valeur et ' +
      'comptes ; mots de passe, codes et champs caches y restent masques tant que l’entree ' +
      'l’est, et chacun se copie ou s’ouvre en grand format sans passer par la page. « Comparer a aujourd’hui » ' +
      'met chaque champ de la version a cote de sa valeur actuelle et marque ce qui differe : c’est exactement ce que ' +
      'la restauration changerait. Dans chaque champ qui differe -- note, identifiant, adresse, nom, et mot de passe une ' +
      'fois affiche --, les mots retires depuis sont barres dans la version et les mots ajoutes soulignes dans la ' +
      'colonne d’aujourd’hui, avec le compte des caracteres retires et ajoutes : un mot de passe dont seul le chiffre ' +
      'final a change se voit d’un coup d’oeil. « Restaurer » reprend le contenu de la version ; ' +
      'le contenu actuel rejoint alors les versions, la restauration se defait donc de la meme facon. Les passkeys, ' +
      'l’archive et la re-demande du mot de passe maitre restent ceux d’aujourd’hui : une version ne ressuscite jamais ' +
      'une passkey retiree. Effacer l’historique des mots de passe efface aussi les versions, qui les contiennent.',
    source: 'apps/web/src/lib/item-history.ts',
  },
  {
    id: 'force-mot-de-passe',
    views: ['generator', 'health', 'entry-editor'],
    question: 'Qu’est-ce qui fait un bon mot de passe ?',
    keywords: ['bon mot de passe', 'force', 'solide', 'longueur', 'entropie', 'combien de caracteres'],
    answer:
      'La longueur d’abord. Le generateur produit par defaut un secret tire d’un generateur cryptographique du ' +
      'systeme ; une phrase de passe diceware de cinq mots ou plus est aussi solide et beaucoup plus facile a ' +
      'retenir — c’est le bon choix pour le mot de passe maitre lui-meme.',
    source: 'packages/crypto-core/README.md',
  },
  {
    id: 'phrase-de-passe',
    views: ['generator'],
    question: 'Phrase de passe ou mot de passe aleatoire ?',
    keywords: ['phrase de passe', 'diceware', 'passphrase', 'mots', 'retenir'],
    answer:
      'Un mot de passe aleatoire pour ce que Klavox retient a votre place ; une phrase de passe pour ce que ' +
      'vous devez taper de tete (mot de passe maitre, session de l’ordinateur). Les mots sont tires de listes ' +
      'publiques EFF (anglais) ou diceware francaise, avec le meme generateur aleatoire. Nombre de mots, langue, ' +
      'separateur et dernier type choisi sont gardes sur cet appareil (des preferences, aucun secret) ; « Generer » ' +
      'dans une fiche suit ce type.',
    source: 'apps/web/src/core/wordlists/README.md',
  },
  {
    id: 'historique-generateur',
    views: ['generator'],
    question: 'J’ai copie un mot de passe genere sans l’enregistrer : comment le retrouver ?',
    keywords: ['historique du generateur', 'genere', 'perdu', 'retrouver', 'inscription', 'pas enregistre', 'copie', 'dernier mot de passe'],
    answer:
      'Generateur, « Historique du generateur » : chaque mot de passe, phrase de passe ou identifiant copie depuis le ' +
      'generateur y est garde 30 jours (100 au plus), masque jusqu’a l’oeil, avec un bouton pour le recopier ou le ' +
      'retirer. Seule la copie compte : les tirages jamais copies n’y entrent pas. L’historique est chiffre avec la cle ' +
      'du coffre sur cet appareil seulement, il n’est ni envoye au serveur ni synchronise ; « Effacer l’historique du ' +
      'generateur » le vide sans toucher au coffre.',
    source: 'apps/web/src/lib/generator-history.ts',
  },
  {
    id: 'contraintes-site-generateur',
    views: ['generator'],
    question: 'Un site refuse certains symboles ou exige des chiffres : comment generer un mot de passe qu’il accepte ?',
    keywords: ['symboles autorises', 'refuse', 'caracteres interdits', 'exclure', 'minimum de chiffres', 'contrainte', 'site n’accepte pas'],
    answer:
      'Generateur, sous les cases : « Chiffres au minimum » et « Symboles au minimum » (0 a 9) imposent leur nombre ; ' +
      '« Symboles autorises » limite les symboles a ceux que le site accepte (laisser vide = tous) ; « Caracteres a ' +
      'exclure » retire ceux qu’il refuse. Si les reglages ne permettent aucun mot de passe, le generateur le dit et garde ' +
      'le dernier valide. Ces reglages sont des preferences, gardes sur cet appareil ; ils ne contiennent aucun secret.',
    source: 'apps/web/src/lib/generator-settings.ts',
  },
  {
    id: 'regles-du-site',
    views: ['entry-editor', 'generator'],
    question: 'Pourquoi « Generer » donne-t-il un mot de passe plus court ou sans symboles pour certains sites ?',
    keywords: ['regles du site', 'regle du site', 'site refuse le mot de passe', 'trop long', 'maxlength', 'password rules', 'safari', 'pour le site'],
    answer:
      'Pour plus de 400 sites qui imposent leurs propres regles (longueur maximale, symboles refuses, chiffre exige), ' +
      '« Generer » dans une fiche suit la regle connue du site, indiquee sous le mot de passe : la longueur du generateur ' +
      'est ramenee dans ses bornes et seuls les caracteres qu’il accepte sont tires, au hasard comme d’habitude. Ces ' +
      'regles viennent de la table publique d’Apple (celle de Safari), embarquee dans Klavox : le site de l’entree n’est ' +
      'envoye nulle part. Sans regle connue, le generateur garde vos reglages. Dans l’ecran Generateur, saisissez le ' +
      'site dans « Pour le site » : le mot de passe tire suit sa regle de la meme facon, et ce site n’est garde qu’en ' +
      'memoire, jamais avec vos reglages.',
    source: 'apps/web/src/lib/site-password-rules.ts',
  },
  {
    id: 'etiquettes',
    views: ['vault-list', 'entry-detail', 'entry-editor'],
    question: 'Comment classer une entree dans plusieurs categories a la fois ?',
    keywords: ['etiquette', 'étiquette', 'tag', 'tags', 'classer', 'categorie', 'catégorie', 'filtrer', 'ranger', 'renommer', 'etiqueter', 'sous-etiquette', 'imbriquee'],
    answer:
      'Avec les etiquettes : dans la fiche, champ « Etiquettes », separees par des virgules (travail, banque, a ' +
      'changer). Une entree n’a qu’un dossier mais autant d’etiquettes que vous voulez. Une barre oblique les ' +
      'imbrique : « Travail/Clients » se range sous « Travail », dont la vue montre aussi ses sous-etiquettes. La barre laterale les ' +
      'liste avec leur nombre d’entrees ; un clic filtre la liste, la recherche les trouve aussi. Les etiquettes ' +
      'des exports 1Password, KeePass et CXF sont reprises telles quelles a l’import. Pour etiqueter plusieurs ' +
      'entrees d’un coup : « Selectionner », cochez-les, saisissez l’etiquette puis « Etiqueter » (ou « Retirer ' +
      'l’etiquette »). Dans la vue d’une etiquette, « Renommer » et « Supprimer l’etiquette » agissent sur tout ' +
      'le coffre, archives et corbeille comprises, sous-etiquettes avec, sans toucher aux entrees elles-memes. Elles sont chiffrees avec ' +
      'l’entree : le serveur ne les voit pas.',
    source: 'docs/ARCHITECTURE.md',
  },
  {
    id: 'cles-ssh',
    views: ['entry-detail', 'entry-editor'],
    question: 'Comment ranger une cle SSH dans le coffre ?',
    keywords: ['ssh', 'cle ssh', 'clé ssh', 'id_ed25519', 'id_rsa', 'empreinte', 'fingerprint', 'cle publique', 'authorized_keys', 'generer', 'ssh-keygen'],
    answer:
      'Nouvelle entree, type « Cle SSH » : « Generer une cle Ed25519 » en cree une dans le navigateur, ou collez la ' +
      'cle privee (fichier id_ed25519, du « -----BEGIN » au « -----END ») ; la cle publique d’une cle OpenSSH s’en ' +
      'deduit, ou collez la ligne du fichier .pub. La fiche ' +
      'montre le type, l’empreinte SHA256 (la meme que « ssh-keygen -l ») et la cle publique a copier dans ' +
      'authorized_keys ; la cle privee reste masquee jusqu’a l’oeil. Une cle publique qui ne va pas avec la cle ' +
      'privee est refusee. Les cles SSH de Bitwarden, 1Password, Proton Pass et des fichiers FIDO CXF sont reprises ' +
      'a l’import, jamais en notes ; l’export CXF les emporte en cle SSH standard (Ed25519, RSA, ECDSA ; une cle protegee par phrase de passe voyage telle quelle). La CLI (« klavox ssh ») les sert a l’agent SSH. Tout est chiffre avec l’entree.',
    source: 'apps/web/src/lib/ssh-key-item.ts',
  },
  {
    id: 'reseau-wifi',
    views: ['entry-detail', 'entry-editor'],
    question: 'Comment ranger le mot de passe d’un reseau Wi-Fi ?',
    keywords: ['wifi', 'wi-fi', 'reseau', 'réseau', 'ssid', 'box', 'routeur', 'borne', 'wpa', 'wpa2', 'wpa3', 'wep', 'cle wifi', 'etiquette', 'coller un code', 'code wifi'],
    answer:
      'Nouvelle entree, type « Reseau Wi-Fi » : le nom du reseau (SSID, espaces compris), la securite (WPA3, WPA2, ' +
      'WPA, WEP ou reseau ouvert), « Reseau masque » si la borne ne diffuse pas son nom, et le mot de passe du ' +
      'reseau, que le bouton a cote peut generer. Une cle qu’aucun appareil n’accepterait est refusee a ' +
      'l’enregistrement (WPA : 8 a 63 caracteres ou 64 chiffres hexadecimaux). La fiche signale un chiffrement WEP ' +
      'ou un reseau ouvert. Le mot de passe beneficie de l’historique et de la Sante du coffre comme celui d’un ' +
      'site. Les routeurs sans fil de 1Password, les reseaux Wi-Fi de Proton Pass et des fichiers FIDO CXF sont ' +
      'repris a l’import ; l’export CXF les emporte en reseau Wi-Fi standard. Le texte d’un code QR Wi-Fi ' +
      '(« WIFI:T:WPA;S:…;P:…;; », lu par l’appareil photo devant l’etiquette d’une box) colle dans le nom du reseau ' +
      'remplit le nom, la cle, la securite et « reseau masque », et le nom de l’entree s’il est vide ; rien n’est ' +
      'enregistre avant « Enregistrer ». Un reseau Entreprise (EAP) n’est pas repris : il se saisit a la main. Tout ' +
      'est chiffre avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'wifi-code-qr',
    views: ['entry-detail'],
    question: 'Comment faire rejoindre mon Wi-Fi a un invite sans lui dicter la cle ?',
    keywords: ['code qr', 'qr code', 'qr', 'partager le wifi', 'partager le reseau', 'invite', 'scanner', 'rejoindre', 'appareil photo', 'connexion wifi', 'imprimer', 'fiche invite', 'affiche'],
    answer:
      'Sur la fiche d’un reseau Wi-Fi, le bouton « Code QR » de la section Reseau Wi-Fi montre le code de ' +
      'connexion : l’invite le vise avec l’appareil photo de son telephone (iPhone, Android) et rejoint le reseau ' +
      'sans saisir la cle. Le code est dessine sur cet appareil, rien n’est envoye nulle part ; il porte le nom du ' +
      'reseau, sa securite, sa cle et « reseau masque » s’il y a lieu. Securite non renseignee : WPA est suppose, ' +
      'et le panneau le dit ; un reseau ouvert ne porte pas de cle. Sans nom de reseau, ou sans cle pour un reseau ' +
      'protege, le panneau dit ce qui manque. Le code contient le mot de passe : ne le montrez qu’a qui peut le ' +
      'recevoir. Echap, la croix ou un clic a cote le ferme ; il se ferme aussi en changeant d’entree et au ' +
      'verrouillage. Une entree qui redemande le mot de passe maitre ne le montre pas avant confirmation. ' +
      '« Imprimer la fiche invite », sous le code, imprime une page a afficher pour vos invites : le code, le nom ' +
      'du reseau et la consigne pour s’y connecter, sans le nom de l’entree ni rien d’autre du coffre. Le code ' +
      'imprime contient la cle : affichez la fiche la ou ce reseau peut etre partage.',
    source: 'apps/web/src/lib/wifi-qr.ts',
  },
  {
    id: 'compte-bancaire',
    views: ['entry-detail', 'entry-editor'],
    question: 'Comment ranger un IBAN et les coordonnees d’un compte bancaire ?',
    keywords: ['banque', 'compte', 'compte bancaire', 'iban', 'bic', 'swift', 'rib', 'virement', 'routing', 'sort code', 'code guichet'],
    answer:
      'Nouvelle entree, type « Compte bancaire » : la banque, le titulaire, la nature du compte, l’IBAN, le BIC, ' +
      'le numero de compte et le code banque pour un pays sans IBAN, et le code d’acces de la banque par telephone. ' +
      'Un IBAN dont la cle de controle ne tient pas (ISO 13616, une faute de frappe suffit) ou un BIC mal forme est ' +
      'refuse a l’enregistrement : aucun virement ne partira vers un compte mal recopie. La fiche masque l’IBAN ' +
      '(pays, cle et quatre derniers caracteres), l’affiche par groupes de quatre a la demande et le copie sans ' +
      'espace. Les comptes bancaires de 1Password sont repris a l’import ; l’export CXF les emporte en champs ' +
      'libelles. Tout est chiffre avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'portefeuille-crypto',
    views: ['entry-detail', 'entry-editor'],
    question: 'Comment ranger la phrase de recuperation d’un portefeuille crypto ?',
    keywords: ['crypto', 'portefeuille', 'wallet', 'bitcoin', 'ethereum', 'seed', 'graine', 'phrase de recuperation', 'mnemonique', 'bip39', 'ledger', 'trezor', 'electrum', 'metamask'],
    answer:
      'Nouvelle entree, type « Portefeuille crypto » : l’application ou le materiel, le reseau, la phrase de ' +
      'recuperation, la phrase de passe BIP39 (le « 25e mot ») s’il y en a une, l’adresse de reception et le mot de ' +
      'passe qui deverrouille l’application. Une phrase BIP39 dont un mot n’est pas dans la liste, ou dont la somme ' +
      'de controle ne tient pas, est refusee a l’enregistrement, avec le mot en cause : une faute de frappe ne se ' +
      'decouvrirait qu’a la restauration, fonds perdus. Les graines Electrum sont reconnues ; une autre langue ou un ' +
      'autre format est garde tel quel, sans jugement. La phrase reste hors de l’ecran tant qu’elle n’est pas ' +
      'revelee, s’affiche alors mot par mot, numerotee, et n’est jamais cherchee. L’export CXF l’emporte masquee. ' +
      'Tout est chiffre avec l’entree.',
    source: 'apps/web/src/lib/recovery-phrase.ts',
  },
  {
    id: 'base-de-donnees',
    views: ['entry-detail', 'entry-editor'],
    question: 'Comment ranger les acces a une base de donnees ?',
    keywords: ['base de donnees', 'database', 'bdd', 'sql', 'postgresql', 'postgres', 'mysql', 'mariadb', 'oracle', 'sql server', 'mongodb', 'redis', 'port', 'hote', 'dsn'],
    answer:
      'Nouvelle entree, type « Base de donnees » : le moteur, le serveur, le port, la base, le SID ou l’alias pour ' +
      'Oracle, les options de connexion, puis l’identifiant et le mot de passe de connexion. Le mot de passe a sa ' +
      'propre ligne : son historique est garde et la Sante du coffre le juge comme celui d’un site. Un port hors de ' +
      '1 a 65535 est refuse, de meme qu’une adresse de connexion collee entiere dans « Serveur » : son mot de passe ' +
      'y serait range hors de sa ligne. Pour PostgreSQL, MySQL, MariaDB, MongoDB et Redis, la fiche compose ' +
      'l’adresse de connexion SANS le mot de passe, a coller dans le client, qui le demandera. Les bases de ' +
      '1Password sont reprises a l’import ; l’export CXF les emporte en champs libelles. Tout est chiffre avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'cle-api',
    views: ['entry-detail', 'entry-editor', 'health'],
    question: 'Comment ranger une cle d’API ou un jeton d’acces ?',
    keywords: ['cle d api', 'api', 'jeton', 'token', 'cle secrete', 'secret', 'bearer', 'github', 'stripe', 'aws', 'rotation', 'expiration', 'pat'],
    answer:
      'Nouvelle entree, type « Cle d’API » : la cle ou le jeton sur sa propre ligne, l’identifiant (ID de cle, compte ' +
      'de service), le type, l’adresse de l’API, les dates de validite et le fichier associe. A chaque rotation, collez ' +
      'la nouvelle cle : l’ancienne rejoint l’historique. Renseignez l’expiration : la Sante du coffre signale la cle ' +
      '30 jours avant et une fois expiree. Les cles des grands services (GitHub, GitLab, Stripe, Slack, AWS, npm, PyPI…) ' +
      'sont reconnues a leur prefixe publie, pour savoir ou la revoquer. Une adresse qui porte la cle (« ?token=… ») ' +
      'est refusee. Les cles d’API de 1Password sont reprises a l’import ; l’export CXF les emporte en cle d’API native. ' +
      'Tout est chiffre avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'licence-logicielle',
    views: ['entry-detail', 'entry-editor', 'health'],
    question: 'Comment ranger une licence de logiciel ?',
    keywords: ['licence', 'license', 'logiciel', 'cle de licence', 'numero de serie', 'cle produit', 'activation', 'abonnement', 'editeur', 'commande', 'maintenance'],
    answer:
      'Nouvelle entree, type « Licence logicielle » : la cle de licence (ou le bloc de licence colle tel quel), la ' +
      'version, l’editeur, le titulaire, l’e-mail d’enregistrement, la page de telechargement, la commande et, pour un ' +
      'abonnement ou une maintenance, la date d’expiration : la Sante du coffre previent 30 jours avant et une fois ' +
      'expiree. La cle reste masquee et n’est jamais cherchee ; « Copier » la lit dans l’entree au clic. Une date ' +
      'illisible, un achat posterieur a l’expiration ou une adresse e-mail invalide sont refuses. Les licences de ' +
      '1Password sont reprises a l’import ; l’export CXF les emporte en champs libelles, la cle masquee. Tout est ' +
      'chiffre avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'serveur',
    views: ['entry-detail', 'entry-editor', 'health'],
    question: 'Comment ranger les acces d’un serveur ?',
    keywords: ['serveur', 'server', 'vps', 'hebergeur', 'hebergement', 'console', 'administration', 'admin', 'ssh', 'panel', 'plesk', 'cpanel'],
    answer:
      'Nouvelle entree, type « Serveur » : l’adresse du serveur, l’identifiant et le mot de passe du compte, puis la ' +
      'console d’administration avec son propre compte et l’hebergeur (site, support, telephone). Le mot de passe du ' +
      'compte a son historique et passe par la Sante du coffre ; celui de la console reste masque, n’est jamais cherche ' +
      'et « Copier » le lit dans l’entree au clic. Une adresse qui porte un mot de passe ou une cle (« ?token=… ») est ' +
      'refusee ; une adresse en http:// est signalee « Non chiffree ». Les serveurs de 1Password sont repris a l’import ' +
      'et l’export CXF les emporte en champs libelles. Tout est chiffre avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'compte-messagerie',
    views: ['entry-detail', 'entry-editor', 'health'],
    question: 'Comment ranger un compte de messagerie (IMAP, SMTP) ?',
    keywords: ['messagerie', 'email', 'e-mail', 'courriel', 'mail', 'imap', 'pop', 'pop3', 'smtp', 'starttls', 'thunderbird', 'outlook'],
    answer:
      'Nouvelle entree, type « Compte de messagerie » : l’adresse ou l’identifiant et le mot de passe, puis les serveurs ' +
      'entrant (IMAP ou POP3) et sortant (SMTP) avec leur port et leur securite, et le fournisseur. Port laisse vide : ' +
      'l’editeur indique celui de la securite choisie (993, 995, 465 ou 587). Le mot de passe a son historique et passe ' +
      'par la Sante du coffre ; un mot de passe SMTP distinct reste masque, n’est jamais cherche et « Copier » le lit dans ' +
      'l’entree au clic. Un serveur sans securite fait circuler le mot de passe en clair : c’est signale « Non chiffree ». ' +
      'Les comptes de 1Password sont repris a l’import et l’export CXF les emporte en champs libelles. Tout est chiffre ' +
      'avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'carte-membre',
    views: ['entry-detail', 'entry-editor', 'health'],
    question: 'Comment ranger une carte de membre ou de fidelite ?',
    keywords: ['membre', 'adhesion', 'adherent', 'fidelite', 'carte de fidelite', 'programme', 'points', 'miles', 'club', 'association', 'cotisation', 'abonnement'],
    answer:
      'Nouvelle entree, type « Carte de membre » : la nature (adhesion ou programme de fidelite), l’organisme, le nom du ' +
      'membre, le numero de membre, un numero complementaire, le code PIN, les dates et les telephones du service client ' +
      'et des reservations. Le numero reste lisible et cherchable, comme sur la carte ; le PIN reste masque, n’est jamais ' +
      'cherche et « Copier » le lit dans l’entree au clic. Dates au mois (03/2027) ou au jour : la Sante du coffre previent ' +
      '30 jours avant l’expiration et une fois expiree. Une date illisible, une carte qui expirerait avant d’etre ouverte, ' +
      'un site qui porte un mot de passe ou un telephone illisible sont refuses. Les adhesions et programmes de fidelite de ' +
      '1Password sont repris a l’import ; l’export CXF les emporte en champs libelles, le PIN masque. Tout est chiffre avec ' +
      'l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'dossier-medical',
    views: ['entry-detail', 'entry-editor'],
    question: 'Comment ranger un dossier medical ?',
    keywords: ['medical', 'sante', 'medecin', 'docteur', 'consultation', 'ordonnance', 'traitement', 'medicament', 'posologie', 'patient', 'hopital', 'praticien'],
    answer:
      'Nouvelle entree, type « Dossier medical » : la date, le professionnel de sante, le lieu, son telephone, le ' +
      'patient, puis le motif de la consultation, le medicament, la posologie et vos remarques. Ce sont des donnees de ' +
      'sante : le motif, le medicament, la posologie et les remarques restent masques, absents de la page tant que vous ' +
      'ne les revelez pas, et ne sont jamais cherches -- retrouvez le dossier par le praticien, le lieu ou le patient. ' +
      'Une date illisible ou un telephone illisible sont refuses. Les dossiers medicaux de 1Password sont repris a ' +
      'l’import ; l’export CXF les emporte en champs libelles, le traitement masque. Tout est chiffre avec l’entree.',
    source: 'apps/web/src/lib/typed-fields.ts',
  },
  {
    id: 'generateur-identifiant',
    views: ['generator', 'entry-editor'],
    question: 'Comment generer un identifiant ou une adresse differente pour chaque site ?',
    keywords: ['identifiant', 'nom d’utilisateur', 'pseudo', 'username', 'sous-adresse', 'plus', 'attrape-tout', 'catch-all', 'domaine', 'nom du site', 'website'],
    answer:
      'Generateur, onglet « Identifiant » : un mot aleatoire (avec 4 chiffres en option), votre adresse avec un ' +
      'suffixe aleatoire (moi+k3x9q2ab@exemple.eu, le courrier arrive dans votre boite habituelle), ou une adresse ' +
      'aleatoire sur votre propre domaine configure en attrape-tout. Un suffixe qui recoit du spam designe le site ' +
      'qui a fuite. Le suffixe peut aussi etre le nom du site de la fiche (moi+github.com@exemple.eu) : plus ' +
      'lisible pour trier le courrier, mais devinable d’un site a l’autre. Le bouton a cote du champ Identifiant ' +
      'd’une fiche utilise ces reglages (et son Site (URL) pour ce suffixe). Tout est tire sur cet ' +
      'appareil ; votre adresse n’est pas memorisee apres le verrouillage.',
    source: 'docs/ARCHITECTURE.md',
  },
  {
    id: 'sante-du-coffre',
    views: ['health', 'vault-list'],
    question: 'Comment le score de sante est-il calcule ?',
    keywords: ['sante', 'score', 'faible', 'reutilise', 'ancien', 'analyse', 'http', 'non chiffre', 'sosie', 'trompeuse', 'hameconnage', 'compromis', 'piratage', 'fuite du site', 'carte', 'expiree', 'passeport', 'permis', 'piece d identite'],
    answer:
      'Entierement sur cet appareil : force estimee (mots de passe courants, prenoms, noms de famille, mots courants du francais et de l’anglais, dates, suites, motifs de clavier et de pave numerique (rangees, colonnes, diagonales), blocs repetes, mots ecrits a l’envers, identifiant et nom du site de l’entree compris), reutilisation entre entrees, anciennete de plus d’un an, ' +
      'adresse en http:// (le mot de passe y circule en clair ; corrigez-la en https:// si le site le permet), ' +
      'adresse trompeuse (« Sosies » : un nom ecrit avec des lettres d’un autre alphabet qui imite un autre site, ' +
      'comme un « а » cyrillique dans apple.com -- un meme nom ne melange jamais deux ecritures, sauf le latin avec le chinois, le japonais ou le coreen, et alors sans lettre accentuee --, ou un nom tout en lettres grecques, armeniennes, georgiennes, ethiopiennes, hebraiques, thaies, indiennes ou birmanes qui imitent le latin, ou un nom de chiffres dont l’un est un caractere qui imite un chiffre, comme le « շ » armenien de 1շ.com, ou un caractere hors de sa zone (« þ » et « ð » islandais hors .is et .fo, « ə » azeri hors .az, point median hors du « l·l » catalan en .cat), ou un signe japonais ou chinois qui imite une barre, un tiret ou un point a cote de lettres latines (google.comノevil.jp, ip一address.com), ou un second point sur un i (pi̇xel.com), ou un caractere qu’un navigateur n’affiche jamais dans une adresse (petite capitale ou lettre phonetique comme ɢᴏᴏɢʟᴇ.com, tiret long, apostrophe, symbole, ecriture ancienne ou d’usage rare), ou un nom qui, a un accent ou un sosie pres, est celui d’un site deja dans le coffre (mabanqué.fr pour mabanque.fr) ou d’un des sites les plus visites (googlé.com pour google.com, łwitter.com pour twitter.com), ou un nom a une lettre ajoutee, otee ou changee pres celui d’un site deja dans le coffre (mabanqe.fr pour mabanque.fr), ou a deux lettres interverties pres celui d’un site du coffre ou d’un des sites les plus visites (mabnaque.fr, goolge.com), ou un nom qui en enchasse un autre devant son vrai domaine -- un des sites les plus visites ou un site deja dans le coffre (google.com-login.net, mabanque.fr.connexion-securisee.net : la barre d’adresse coupee ne montre que le debut), ou le nom d’une grande marque ou d’un site deja dans le coffre accole a un mot comme login, secure, account ou wallet (google-login.com, youtubesecure.com, mabanque-secure.fr) -- le mot de passe a sans doute ete saisi sur un site d’hameconnage : ' +
      'changez-le sur le vrai site et corrigez l’adresse ; l’editeur le signale des la saisie de l’adresse, et la fiche ' +
      'marque l’adresse en cause), ' +
      'site compromis (le site a subi une fuite de mots de passe apres le dernier changement de celui de l’entree, ' +
      'ou apres sa creation s’il n’a jamais change : changez-le sur le site ; liste des fuites publiee par Have I Been Pwned, ' +
      'la date d’un mot de passe importe de Bitwarden (JSON), de 1Password (.1pux ou .1pif), de KeePass (historique de l’entree), d’Enpass (JSON), de Keeper (JSON), de F-Secure (.fsk), de Firefox (CSV), de Proton Pass (JSON ou CSV), de Psono (JSON), de Passman (JSON), de Password Safe (XML), de SafeInCloud (XML), de Password Depot (XML), de Sticky Password (XML) ou d’un ' +
      'fichier CXF est reprise de l’export, pas du jour de l’import, et l’export CXF de Klavox la conserve ; ' +
      'licence CC BY 4.0, embarquee dans l’application -- vos sites ne sont jamais envoyes), ' +
      'absence de second facteur sur un site connu pour le proposer, et presence dans une fuite si la ' +
      'verification est activee. Aucun mot de passe n’est envoye pour obtenir ce score. Les cartes bancaires ' +
      'expirees ou expirant dans les 60 jours sont listees a part, hors du score (une carte archivee ne l’est pas), ' +
      'comme les passeports, cartes d’identite et permis expires ou expirant dans les 6 mois.',
    source: 'docs/DATA_PROTECTION.md',
  },
  {
    id: 'echeance-mot-de-passe',
    views: ['entry-editor', 'entry-detail', 'health'],
    question: 'Comment me rappeler de changer un mot de passe a une date donnee ?',
    keywords: ['echeance', 'expiration', 'expire', 'changer avant', 'rappel', 'rotation', 'renouveler', 'a changer', 'keepass', 'tous les 90 jours', 'periodique', 'politique'],
    answer:
      'Dans l’editeur, « Changer le mot de passe avant le » fixe une date, ou un delai d’un clic (1, 3, 6 mois, 1 an) : ' +
      'utile pour un mot de passe provisoire, un compte partage ou une regle d’entreprise. La fiche affiche l’echeance, ' +
      'et la Sante du coffre liste a part, hors du score, les mots de passe dont l’echeance est passee ou tombe sous ' +
      '14 jours (tuile « A changer »). Changer le mot de passe acquitte une echeance passee ou proche : elle est retiree a ' +
      'l’enregistrement, et vous pouvez en fixer une nouvelle dans la foulee. Une echeance lointaine reste. Pour une regle ' +
      'qui revient (« tous les 90 jours »), choisissez « Renouveler » : l’echeance est posee a l’enregistrement, puis ' +
      'reportee de la meme duree a chaque changement du mot de passe. La date et la regle sont ' +
      'chiffrees avec l’entree, jamais visibles du serveur ; l’echeance d’une entree KeePass (« Expire ») est reprise a l’import.',
    source: 'apps/web/src/lib/password-expiry.ts',
  },
  {
    id: 'sante-doublons',
    views: ['health'],
    question: 'Que faire des entrees en double signalees par la Sante du coffre ?',
    keywords: ['doublon', 'double', 'copie', 'deux fois', 'import', 'fusionner', 'nettoyer'],
    answer:
      'Deux entrees du meme type pour le meme site, avec le meme identifiant et le meme mot de passe, sont le plus ' +
      'souvent un import fait deux fois. Chaque copie montre ce qu’elle porte en propre (passkey, code 2FA, notes, ' +
      'champs) : « Fusionner ici » garde une copie et y reunit ce que portent les autres -- adresses, notes, champs, ' +
      'passkeys, historique, pieces jointes ; une valeur qui differe (deux graines 2FA) devient un champ masque, ' +
      'jamais ecrasee -- puis met les autres a la corbeille, d’ou elles se restaurent pendant 30 jours. Le groupe disparait des qu’il ne reste qu’une copie : la derniere n’est jamais proposee. Une entree ' +
      'archivee ou exclue de la Sante n’est pas comparee, et une entree sensible redemande le mot de passe maitre. ' +
      'Ce constat ne compte pas dans le score, mais tant que les copies restent, leur mot de passe passe pour reutilise.',
    source: 'apps/web/src/lib/vault-health.ts',
  },
  {
    id: 'sante-ancien-domaine',
    views: ['health'],
    question: 'Pourquoi la Sante du coffre propose-t-elle de mettre une adresse a jour ?',
    keywords: ['ancien domaine', 'nouveau domaine', 'adresse a mettre a jour', 'change de nom', 'twitter', 'x.com', 'renomme', 'domaine abandonne'],
    answer:
      'Le service de cette adresse a quitte son domaine (twitter.com est devenu x.com, transferwise.com wise.com) : ' +
      'la table d’Apple que suit Safari le dit, embarquee, jamais interrogee en ligne. L’identifiant reste propose sur ' +
      'le nouveau domaine ; « Mettre a jour l’adresse » remplace l’ancienne par https:// suivi du nouveau domaine (le ' +
      'chemin n’est pas repris : rien ne dit qu’il existe sous le nouveau nom), et l’ancienne reste dans les versions ' +
      'de l’entree. L’entree retrouve ainsi son lien, et les verifications du site actuel (2FA, passkeys, fuites). Un ' +
      'domaine encore en service n’est jamais signale ; une entree archivee, exclue de la Sante ou retiree des domaines ' +
      'equivalents non plus. Une entree sensible redemande le mot de passe maitre. Hors score.',
    source: 'apps/web/src/lib/vault-health.ts',
  },
  {
    id: 'sante-2fa-disponible',
    views: ['health'],
    question: 'Comment Klavox sait-il qu’un site propose la double authentification ?',
    keywords: ['2fa', 'double authentification', 'second facteur', 'totp', 'disponible', 'activer', '2fa.directory'],
    answer:
      'Par la liste publique 2fa.directory des sites qui acceptent un code a usage unique (TOTP), embarquee dans ' +
      'Klavox : l’analyse ne contacte aucun service et vos sites ne quittent pas l’appareil. Le bouton « Activer la ' +
      '2FA » ouvre la marche a suivre publiee par le site ; ajoutez ensuite la cle du code dans l’entree, le badge ' +
      'disparait.',
    source: 'apps/web/src/lib/twofa-directory-data.ts',
  },
  {
    id: 'sante-passkeys-disponibles',
    views: ['health'],
    question: 'Pourquoi Klavox me propose-t-il de creer une passkey ?',
    keywords: ['passkey', 'cle d’acces', 'sans mot de passe', 'webauthn', 'disponible', 'passkeys.directory'],
    answer:
      'Le site figure dans la liste publique passkeys.directory des sites qui acceptent les passkeys, embarquee ' +
      'dans Klavox : l’analyse ne contacte aucun service. Une passkey ne se tape pas et ne sert que sur le vrai ' +
      'site : un faux site ne peut pas la detourner. Le bouton « Creer une passkey » ouvre la marche a suivre du ' +
      'site ; si vous la rangez dans une cle de securite ou le trousseau du systeme, indiquez « Deja fait ». Ce ' +
      'conseil ne compte pas dans le score.',
    source: 'apps/web/src/lib/passkeys-directory-data.ts',
  },
  {
    id: 'fuites',
    views: ['health', 'settings'],
    question: 'La verification des fuites envoie-t-elle mes mots de passe ?',
    keywords: ['fuite', 'breach', 'pwned', 'compromis', 'hibp', 'divulgue'],
    answer:
      'Non. Le controle utilise le k-anonymat : seuls les cinq premiers caracteres de l’empreinte SHA-1 partent, ' +
      'et la comparaison finale se fait sur l’appareil. La verification se fait chez nous, en Europe, sur notre ' +
      'miroir fuites.santvia.eu (sans journal) : aucun service tiers n’est interroge. Les donnees sont celles de ' +
      'Have I Been Pwned (Troy Hunt), licence CC BY 4.0. Il est desactive par defaut ; un miroir indisponible ' +
      'donne « verification impossible », jamais « aucune fuite ».',
    source: 'docs/DATA_PROTECTION.md',
  },
  {
    id: 'import-export',
    views: ['settings'],
    question: 'Comment importer mes mots de passe depuis un autre gestionnaire ?',
    keywords: ['importer', 'import', 'exporter', 'export', 'csv', 'aegis', '2fas', 'google authenticator', 'proton authenticator', 'ente auth', 'ente', 'authenticator', 'otpauth', 'bitwarden', 'lastpass', 'dashlane', '1password', '1pif', 'keepass', 'keepassxc', 'kdbx', 'fichier-cle', 'fichier cle', 'keyx', 'kdb', 'keepassx', 'keeweb', 'strongbox', 'passbolt', 'buttercup', 'padloc', 'proton', 'nordpass', 'keeper', 'roboform', 'enpass', 'zoho', 'kaspersky', 'avast', 'psono', 'myki', 'jumpcloud', 'avira', 'password boss', 'passwordboss', 'sticky password', 'stickypassword', 'password depot', 'passworddepot', 'safeincloud', 'safe in cloud', 'password safe', 'passwordsafe', 'pwsafe', 'delinea', 'secret server', 'thycotic', 'password dragon', 'passworddragon', 'password xp', 'passwordxp', 'gnome', 'seahorse', 'trousseau', 'keyring', 'clipperz', 'html', 'msecure', 'codebook', 'zetetic', 'netwrix', 'password secure', 'mateso', 'encryptr', 'spideroak', 'remembear', 'passpack', 'blur', 'abine', 'meldium', 'yoti', 'logmeonce', 'passkeep', 'saferpass', 'universal password manager', 'upm', 'password agent', 'passwordwallet', 'splashid', 'ascendo', 'datavault', 'blackberry', 'sans en-tete', 'tunnelbear', 'passky', 'passman', 'nextcloud', 'f-secure', 'fsecure', 'fsk', 'true key', 'truekey', 'mcafee', 'xml', 'sauvegarde', 'cxf', 'fido', 'migrer'],
    answer:
      'Parametres accepte, choisi ou depose sur le bloc d’import, KeePass (.kdbx, XML ou CSV), KeePass 1.x / KeePassX (.kdb), Passbolt, Buttercup, Padloc, Bitwarden JSON (chiffre compris), Proton Pass, 1Password (.1pux, .1pif ou ' +
      'CSV), LastPass, Dashlane (CSV, .zip ou JSON), NordPass, Keeper, RoboForm, Enpass, Zoho Vault, Kaspersky, Avast, Psono, Myki, Avira, Password Boss, Passky, Passman (Nextcloud), F-Secure, Sticky Password, Password Depot, SafeInCloud, Password Safe, Delinea Secret Server (Thycotic), Password Dragon, Password XP, GNOME Passwords (Seahorse, trousseau du bureau Linux), Clipperz, mSecure, Codebook, Netwrix Password Secure, Encryptr, RememBear, Passpack, Blur (Abine), Meldium, Yoti, LogMeOnce, PassKeep, SaferPass, McAfee True Key, Chrome, Edge, Firefox et le CSV generique ; les exports sans en-tete de Universal Password Manager, Password Agent, PasswordWallet, SplashID Safe et Ascendo DataVault, et celui de BlackBerry Password Keeper, en choisissant leur format au-dessus du bouton d’import ; les codes 2FA d’Aegis, de 2FAS (sauvegardes chiffrees comprises), de Google Authenticator (captures d’ecran des QR codes « Transferer des comptes » choisies toutes ensemble avec « Lire des captures de codes QR », deposees ensemble sur le bloc d’import ou collees (Ctrl+V) -- un code du transfert oublie n’importe rien et le dit --, ou leur texte), de Proton Authenticator (chiffre compris), d’Ente Auth et toute liste d’URI otpauth:// rejoignent aussi le coffre chiffre — et propose l’export chiffre du ' +
      'coffre, jamais un export en clair par defaut. Dossiers, historique des mots de passe et pieces jointes ' +
      'sont repris quand le format les porte. Si le fichier est chiffre, Klavox demande SON mot de passe (et, pour ' +
      'une base KeePass, son fichier-cle s’il en a un ; fichier-cle seul : mot de passe laisse vide) : il ' +
      'sert a le lire sur cet appareil, il n’est ni conserve ni televerse. Pour partir ailleurs, l’export ' +
      'FIDO CXF est le format d’echange standard que les autres gestionnaires relisent : il est scelle par un ' +
      'mot de passe d’export que vous choisissez, jamais ecrit en clair ; une jauge le mesure a la frappe et, ' +
      'juge faible (il se devine hors ligne, l’e-mail du compte et « klavox » compris), il n’est accepte qu’en cochant ' +
      '« Utiliser ce mot de passe faible quand meme ». Une entree archivee ou qui redemande ' +
      'le mot de passe maitre le reste quand vous reimportez cet export dans Klavox.',
    source: 'apps/web/src/lib/kdbx-import.ts',
  },
  {
    id: 'cle-de-securite',
    views: ['settings'],
    question: 'A quoi sert une cle de securite WebAuthn ici ?',
    keywords: ['cle de securite', 'webauthn', 'yubikey', 'passkey', 'second facteur du compte'],
    answer:
      'Elle protege la CONNEXION au serveur de synchro, pas le dechiffrement : meme avec votre session, un ' +
      'attaquant n’obtiendrait que du chiffre. Ajouter ou revoquer un authentificateur exige la preuve du mot ' +
      'de passe maitre.',
    source: 'packages/webauthn-core/README.md',
  },
  {
    id: 'application-authentification-compte',
    views: ['settings'],
    question: 'Comment proteger la connexion par une application d’authentification (code a 6 chiffres) ?',
    keywords: ['application d authentification', 'authenticator', 'totp', 'code a 6 chiffres', 'google authenticator', 'aegis', '2fa', 'double authentification', 'second facteur'],
    answer:
      'Reglages, « Double authentification », « Activer l’application d’authentification » : scannez le code QR ' +
      'avec Aegis, 2FAS, Google ou Microsoft Authenticator, puis tapez le code affiche et confirmez par votre mot de ' +
      'passe maitre. Ensuite, chaque connexion d’un appareil au serveur demande ce code. Il protege la connexion, ' +
      'pas le dechiffrement ; un code ne sert qu’une fois, et cinq codes faux obligent a redonner le mot de passe maitre. ' +
      'Le retirer exige aussi le mot de passe maitre. Gardez une cle de securite ou la cle de recuperation en secours.',
    source: 'apps/server/src/account-totp.ts',
  },
  {
    id: 'compte-idenva',
    views: ['settings'],
    question: 'Que change le fait de lier mon compte Idenva ?',
    keywords: ['idenva', 'lier mon compte', 'delier', 'sso', 'identite santvia', 'oidc', 'compte unique'],
    answer:
      'Le serveur Klavox retient votre identifiant Idenva, jamais votre adresse e-mail ; une deconnexion Idenva ' +
      'ferme alors aussi vos sessions Klavox. Rien ne passe au coffre : le mot de passe maitre ne part jamais ' +
      'chez Idenva et reste la seule cle de dechiffrement. Lier et delier exigent la preuve du mot de passe maitre.',
    source: 'docs/IDENVA-INTEGRATION.md',
  },
  {
    id: 'changer-email',
    views: ['settings'],
    question: 'Comment changer l’adresse e-mail de mon compte ?',
    keywords: ['changer email', 'changer adresse', 'nouvelle adresse', 'modifier email', 'adresse e-mail', 'identifiant de connexion', 'courriel'],
    answer:
      'Reglages, « Adresse e-mail du compte » : saisissez la nouvelle adresse et votre mot de passe maitre. L’adresse ' +
      'sert de sel a la derivation de votre cle : Klavox re-scelle la cle du coffre sous la nouvelle, sur cet appareil, ' +
      'et n’envoie au serveur que des preuves, jamais le mot de passe maitre. Le mot de passe ne change pas, aucune ' +
      'entree n’est rechiffree, la cle de recuperation reste valable. Connecte a un serveur, vos autres appareils sont ' +
      'deconnectes : reconnectez-les avec la nouvelle adresse. Une adresse deja prise par un autre compte est refusee, ' +
      'et rien n’est modifie.',
    source: 'apps/web/src/lib/account-email-change.ts',
  },
  {
    id: 'appareils-connectes',
    views: ['settings'],
    question: 'J’ai perdu un appareil : comment le deconnecter de mon compte ?',
    keywords: ['appareil perdu', 'appareil vole', 'telephone perdu', 'deconnecter', 'deconnecter tous', 'autres appareils', 'sessions', 'session volee', 'appareils connectes', 'liste des appareils'],
    answer:
      'Reglages, « Appareils connectes », « Afficher les appareils connectes » : chaque appareil relie au compte, son ' +
      'navigateur, sa date de connexion et sa derniere activite ; « Deconnecter » sur sa ligne ferme UN appareil, les ' +
      'autres restent connectes. Pour tous d’un coup : « Deconnecter tous mes autres appareils », puis confirmez par le mot de passe ' +
      'maitre. Le serveur ferme les sessions de tous les appareils relies au compte, y compris leurs jetons de ' +
      'renouvellement ; cet appareil-ci recoit une session neuve et reste connecte. Les autres devront redonner le ' +
      'mot de passe maitre avant de synchroniser. Le coffre ne bouge pas. Si le mot de passe maitre lui-meme a pu etre ' +
      'vu, changez-le : cela deconnecte aussi tous les autres appareils.',
    source: 'apps/server/src/server.ts',
  },
  {
    id: 'activite-compte',
    views: ['settings'],
    question: 'Quelqu’un a-t-il essaye d’entrer dans mon compte ?',
    keywords: ['activite', 'activite du compte', 'journal', 'historique de connexion', 'connexions', 'tentative', 'connexion suspecte', 'adresse ip', 'qui s est connecte'],
    answer:
      'Reglages, « Activite du compte », « Afficher l’activite recente » : le serveur rend ce qu’il a vu de votre ' +
      'compte ces 90 derniers jours -- connexions reussies et ratees, cles de securite, recuperation, changements ' +
      'sensibles -- avec la date, le navigateur et l’adresse IP. Une tentative ratee est signalee en tete. Si une ' +
      'ligne ne vient pas de vous, changez votre mot de passe maitre puis deconnectez tous vos autres appareils. ' +
      'Le journal ne contient aucun secret : ni mot de passe, ni contenu du coffre.',
    source: 'apps/server/src/security-events.ts',
  },
  {
    id: 'suppression-compte',
    views: ['settings'],
    question: 'Comment supprimer mon compte, et que devient mon coffre ?',
    keywords: ['supprimer mon compte', 'supprimer le compte', 'fermer mon compte', 'effacer mon compte', 'droit a l effacement', 'rgpd', 'resilier'],
    answer:
      'Reglages, « Supprimer mon compte serveur » : recopiez votre adresse e-mail puis confirmez par le mot de passe ' +
      'maitre. Le serveur efface alors votre e-mail, le coffre chiffre, vos envois, vos delegations d urgence, vos cles ' +
      'de securite, la liaison Idenva et les sessions de tous vos appareils. Le coffre de cet appareil reste, sauf si ' +
      'vous cochez « Effacer aussi le coffre de cet appareil » : exportez-le d abord si vous voulez le garder ailleurs.',
    source: 'docs/DATA_PROTECTION.md',
  },
  {
    id: 'deverrouillage-passkey',
    views: ['auth-unlock', 'settings'],
    question: 'Puis-je ouvrir mon coffre avec mon empreinte au lieu du mot de passe maitre ?',
    keywords: [
      'deverrouiller avec une passkey',
      'passkey de deverrouillage',
      'biometrie',
      'empreinte',
      'touch id',
      'face id',
      'windows hello',
      'sans mot de passe',
      'ouvrir sans mot de passe maitre',
    ],
    answer:
      'Oui, sur cet appareil : Parametres > « Deverrouillage par passkey ». Votre appareil (Touch ID, Windows Hello, ' +
      'cle de securite) derive alors un secret qui ne sort jamais de lui, et ce secret scelle une copie de la cle de ' +
      'ce coffre — ici seulement, jamais sur le serveur ni dans un export. A ne pas confondre avec la cle de ' +
      'securite du compte, qui protege la connexion au serveur : ici c’est le coffre qui s’ouvre. Le mot de passe ' +
      'maitre reste la racine — il ouvre toujours le coffre, il est le seul a fonctionner sur un nouvel appareil, et ' +
      'revoquer la passkey ne modifie aucune de vos entrees.',
    source: 'apps/web/src/lib/passkey-unlock.ts',
  },
  {
    id: 'passkeys-stockees',
    views: ['entry-detail', 'vault-list'],
    question: 'Klavox garde-t-il les passkeys de mes comptes ?',
    keywords: [
      'stocker une passkey',
      'garder mes passkeys',
      'passkey de mes comptes',
      'importer mes passkeys',
      'passkey github',
      'se connecter avec une passkey',
    ],
    answer:
      'Oui pour les CONSERVER : une passkey importee (fichier FIDO CXF d’Apple Passwords, Google Password Manager, ' +
      '1Password, Bitwarden…) est scellee dans l’entree comme un mot de passe — sa cle privee n’est ni affichee, ni ' +
      'copiable, ni envoyee au serveur — et elle repart intacte dans un export CXF : aucun enfermement. Pas encore ' +
      'pour les UTILISER : presenter une passkey a un site demande une extension navigateur ou l’application mobile, ' +
      'qui viendront apres. D’ici la, gardez l’acces d’origine a ces comptes.',
    source: 'apps/web/src/lib/passkey-credential.ts',
  },
  {
    id: 'suppression',
    views: ['entry-detail', 'settings'],
    question: 'Que devient une entree supprimee ?',
    keywords: ['supprime', 'suppression', 'efface', 'corbeille', 'restaurer', 'vider', 'tombstone'],
    answer:
      '« Supprimer » la place dans la Corbeille : hors des listes, de la recherche, de la Sante du coffre et de ' +
      'l’export, restaurable telle quelle (dossier, pieces jointes, historique) pendant 30 jours. Le choix est ' +
      'chiffre avec l’entree : le serveur ne sait pas ce qui est a la corbeille. Passe ce delai, ou par « Supprimer ' +
      'definitivement » / « Vider la corbeille », elle laisse une marque de suppression (revision + date, sans ' +
      'contenu) que les autres appareils appliquent a la synchro suivante ; le chiffre est retire, il n’y a pas de ' +
      'copie cachee cote serveur.',
    source: 'apps/web/src/lib/entry-trash.ts',
  },
  {
    id: 'dossiers-favoris',
    views: ['vault-list'],
    question: 'Comment organiser mes entrees ?',
    keywords: ['dossier', 'favori', 'ranger', 'classer', 'organiser', 'rechercher', 'accent', 'avancee', 'exclure'],
    answer:
      'Par dossiers (crees depuis la barre laterale) et par favoris, et surtout par la recherche : elle filtre ' +
      'sur les entrees dechiffrees en memoire, donc elle voit les noms et identifiants sans qu’aucun index ne ' +
      'soit ecrit en clair. Elle ignore la casse et les accents (« societe » trouve « Société »), et plusieurs ' +
      'mots se cherchent ensemble, chacun dans n’importe quel champ (« ana banque »), champs personnalises compris ' +
      '-- leur nom, et la valeur des seuls champs visibles : jamais celle d’un champ masque ou d’un code. ' +
      'Commencee par « > », elle devient avancee : « >nom:banque » ne regarde que le nom (aussi identifiant:, ' +
      'site:, notes:, etiquette:, champ:), « -perso » exclut, et « "lyon bellecour" » cherche l’expression exacte.',
    source: 'packages/vault-model/README.md',
  },
  {
    id: 'changer-mot-de-passe-maitre',
    views: ['settings', 'auth-recover'],
    question: 'Comment changer mon mot de passe maitre ?',
    keywords: ['changer', 'modifier mon mot de passe maitre', 'nouveau mot de passe maitre'],
    answer:
      'Parametres > « Mot de passe maitre » : l’actuel, le nouveau, sa confirmation. Le changement re-scelle la ' +
      'cle du coffre avec la nouvelle derivation : le contenu n’est pas rechiffre, seule l’enveloppe change, et ' +
      'votre cle de recuperation reste valable. Si la synchro serveur est active, vos autres appareils sont ' +
      'deconnectes et redemanderont le nouveau mot de passe.',
    source: 'apps/web/src/lib/master-password-change.ts',
  },
  {
    id: 'aide-elle-meme',
    views: [
      'auth-create',
      'auth-unlock',
      'auth-recover',
      'auth-recover-remote',
      'vault-list',
      'entry-detail',
      'entry-editor',
      'generator',
      'settings',
      'health',
    ],
    question: 'Cette aide voit-elle le contenu de mon coffre ?',
    keywords: ['aide', 'kopilvia', 'assistant', 'ia', 'confidentialite de l aide', 'ce que tu vois'],
    answer:
      'Elle lit des compteurs et des indicateurs (coffre verrouille ou non, nombre d’entrees, synchro ' +
      'configuree, cle de recuperation activee) — jamais un nom d’entree ni un mot de passe. Elle n’appelle ' +
      'aucun service : votre question reste sur cet appareil, et une reponse qu’elle ne peut pas sourcer, elle ' +
      'ne la donne pas.',
    source: 'CDC.md',
  },
];

/** Instantane de l'application : uniquement des compteurs et des indicateurs. */
export interface AccountSnapshot {
  readonly locked: boolean;
  /** Une enveloppe de compte existe sur cet appareil. */
  readonly hasLocalVault: boolean;
  readonly entryCount: number;
  readonly folderCount: number;
  readonly recoveryKeyEnabled: boolean;
  /** Passkeys qui deverrouillent le coffre sur CET appareil (R12). */
  readonly passkeyUnlockCount: number;
  /** URL du serveur de synchro (donnee publique) ; `null` = coffre local seul. */
  readonly syncServer: string | null;
  /** Seconds facteurs actifs du compte serveur (authentificateurs WebAuthn + application TOTP) ; `null` = inconnu. */
  readonly twoFactorCount: number | null;
  readonly breachCheckEnabled: boolean;
  /** Compteurs de « Sante du coffre » ; `null` = rien a analyser. */
  readonly health: {
    readonly score: number;
    readonly weak: number;
    readonly reused: number;
    readonly old: number;
    readonly breached: number;
    /** Entrees dont une adresse est en `http://`. */
    readonly insecureUri: number;
    /** Cartes bancaires expirees ou expirant sous 60 jours (hors archives). */
    readonly cardsToRenew: number;
    /** Pieces d'identite (passeport, carte, permis) expirees ou expirant sous 6 mois (hors archives). */
    readonly documentsToRenew: number;
  } | null;
}

export interface AccountFact {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  /** `attention` = quelque chose demande une action de l'utilisateur. */
  readonly level: 'ok' | 'attention' | 'info';
  readonly keywords: readonly string[];
}

/** La source citee quand la reponse vient du compte et non de la documentation. */
export const ACCOUNT_SOURCE = 'etat de votre compte, lu sur cet appareil';

/**
 * L'etat reel du compte, en faits citables. C'est la lecon du 23/09 sur
 * Vigilvia : une aide qui ignore que SA boite est en erreur ne sert a rien.
 */
export function accountFacts(snapshot: AccountSnapshot): readonly AccountFact[] {
  const facts: AccountFact[] = [
    {
      id: 'coffre',
      label: 'Coffre',
      value: snapshot.locked
        ? snapshot.hasLocalVault
          ? 'verrouille sur cet appareil'
          : 'aucun coffre sur cet appareil — il reste a creer'
        : `ouvert — ${snapshot.entryCount} entree(s), ${snapshot.folderCount} dossier(s)`,
      level: snapshot.locked ? 'info' : 'ok',
      keywords: ['coffre', 'verrouille', 'ouvert', 'combien d entrees', 'nombre'],
    },
    {
      id: 'recuperation',
      label: 'Cle de recuperation',
      value: snapshot.recoveryKeyEnabled
        ? 'activee — vous pouvez redefinir votre mot de passe maitre'
        : 'NON activee — si vous oubliez votre mot de passe maitre, le coffre sera definitivement illisible',
      level: snapshot.recoveryKeyEnabled ? 'ok' : 'attention',
      keywords: ['cle de recuperation', 'recuperation', 'oubli', 'secours', 'perdre'],
    },
    {
      id: 'synchro',
      label: 'Synchronisation',
      value:
        snapshot.syncServer === null
          ? 'aucun serveur configure — le coffre vit sur cet appareil uniquement, et n’est sauvegarde nulle part'
          : `connectee a ${snapshot.syncServer}`,
      level: snapshot.syncServer === null ? 'info' : 'ok',
      keywords: ['synchro', 'synchronisation', 'serveur', 'sauvegarde', 'autres appareils'],
    },
    {
      id: 'fuites',
      label: 'Verification des fuites',
      value: snapshot.breachCheckEnabled ? 'activee (k-anonymat)' : 'desactivee',
      level: 'info',
      keywords: ['fuite', 'breach', 'pwned', 'compromis'],
    },
  ];
  facts.push({
    id: 'passkey-deverrouillage',
    label: 'Deverrouillage par passkey',
    value:
      snapshot.passkeyUnlockCount === 0
        ? 'aucune passkey sur cet appareil — le coffre s’ouvre avec le mot de passe maitre'
        : `${snapshot.passkeyUnlockCount} passkey(s) sur cet appareil — le mot de passe maitre reste la racine et ouvre toujours`,
    level: 'info',
    keywords: ['passkey', 'biometrie', 'touch id', 'windows hello', 'empreinte', 'face id', 'sans mot de passe'],
  });
  if (snapshot.twoFactorCount !== null) {
    facts.push({
      id: 'double-facteur',
      label: 'Second facteur du compte',
      value:
        snapshot.twoFactorCount === 0
          ? 'aucun — la connexion au serveur repose sur le seul mot de passe'
          : `${snapshot.twoFactorCount} actif(s)`,
      level: snapshot.twoFactorCount === 0 ? 'attention' : 'ok',
      keywords: ['cle de securite', 'webauthn', 'double facteur', '2fa', 'passkey', 'totp', 'application d authentification'],
    });
  }
  if (snapshot.health !== null) {
    const aTraiter =
      snapshot.health.weak +
      snapshot.health.reused +
      snapshot.health.breached +
      snapshot.health.old +
      snapshot.health.insecureUri +
      snapshot.health.cardsToRenew +
      snapshot.health.documentsToRenew;
    facts.push({
      id: 'sante',
      label: 'Sante du coffre',
      value:
        aTraiter === 0
          ? `score ${snapshot.health.score}/100 — rien a traiter`
          : `score ${snapshot.health.score}/100 — ${snapshot.health.breached} dans une fuite, ` +
            `${snapshot.health.weak} faible(s), ${snapshot.health.reused} reutilise(s), ${snapshot.health.old} ancien(s), ` +
            `${snapshot.health.insecureUri} sur un site non chiffre, ${snapshot.health.cardsToRenew} carte(s) a renouveler, ` +
            `${snapshot.health.documentsToRenew} piece(s) d'identite a renouveler`,
      level: aTraiter === 0 ? 'ok' : 'attention',
      keywords: ['sante', 'score', 'faible', 'reutilise', 'ancien', 'probleme', 'http', 'non chiffre', 'carte', 'expiree', 'passeport', 'permis', 'piece d identite'],
    });
  }
  return facts;
}

export interface HelpAnswer {
  /** `inconnu` = « je ne sais pas » : une reponse valide et exigee par le CDC. */
  readonly kind: 'documentation' | 'compte' | 'inconnu';
  readonly title: string;
  readonly text: string;
  /** Vide UNIQUEMENT quand `kind` vaut `inconnu`. */
  readonly sources: readonly string[];
  /** Questions proposees en rebond (toujours tirees du catalogue). */
  readonly related: readonly string[];
}

const DIACRITIQUES = /[̀-ͯ]/gu;

/**
 * Minuscules, sans accent ni ponctuation, et au singulier : « Où sont mes
 * pièces jointes ? » -> « ou sont me piece jointe ». Le pluriel est retire des
 * DEUX cotes (question et mot-cle), donc la troncature n'a pas a etre juste en
 * francais : elle a seulement a etre la meme.
 */
export function normaliser(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(DIACRITIQUES, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/gu, ' ')
    .trim()
    .split(' ')
    .map((mot) => (mot.length >= 4 && mot.endsWith('s') ? mot.slice(0, -1) : mot))
    .join(' ');
}

export function helpTopicsForView(view: HelpViewId): readonly HelpTopic[] {
  return HELP_TOPICS.filter((topic) => topic.views.includes(view));
}

/** Les questions proposees sur une vue (l'utilisateur peut aussi ecrire la sienne). */
export function helpSuggestions(view: HelpViewId, limit = 4): readonly string[] {
  return helpTopicsForView(view).slice(0, limit).map((topic) => topic.question);
}

function score(question: string, keywords: readonly string[]): number {
  const normalisee = normaliser(question);
  let meilleur = 0;
  for (const keyword of keywords) {
    const cle = normaliser(keyword);
    if (cle === '') continue;
    // Prefixe de MOT : « synchro » reconnait « synchronise », sans reconnaitre
    // un mot qui le contiendrait en son milieu. Aucun echappement n'est requis :
    // apres normalisation, un mot-cle ne contient que [a-z0-9 ].
    const motif = new RegExp(`(^| )${cle}[a-z0-9]*( |$)`, 'u');
    const trouve = motif.exec(normalisee);
    if (trouve === null) continue;
    // La MEILLEURE correspondance, pas leur somme : « synchro » et « sync »
    // designent la meme chose et ne doivent pas compter deux fois. Un mot-cle
    // long prime sur un mot isole (« oublie mon mot de passe maitre » sur
    // « mot de passe maitre ») ; a egalite de mots, la correspondance la plus
    // litterale l'emporte.
    meilleur = Math.max(meilleur, cle.split(' ').length + trouve[0].trim().length / 100);
  }
  return meilleur;
}

/**
 * Le coeur conversationnel — et le verrou anti-confabulation. Toute reponse
 * sort soit du catalogue, soit de l'etat du compte ; rien n'est synthetise.
 * En dessous du seuil de correspondance, la reponse est « je ne sais pas ».
 */
export function askHelp(question: string, view: HelpViewId, snapshot: AccountSnapshot): HelpAnswer {
  const suggestions = helpSuggestions(view);
  const pose = normaliser(question);
  if (pose.length < 3) {
    return {
      kind: 'inconnu',
      title: 'Posez votre question',
      text: `Ecrivez votre question avec vos mots. ${HELP_ESCALATION}`,
      sources: [],
      related: suggestions,
    };
  }

  const faits = accountFacts(snapshot);
  const meilleurFait = faits
    .map((fait) => ({ fait, points: score(question, fait.keywords) }))
    .sort((a, b) => b.points - a.points)[0];
  const meilleurSujet = HELP_TOPICS
    .map((topic) => {
      const points = score(question, topic.keywords);
      // Bonus de contexte : a egalite, le sujet de la vue ou l'utilisateur est
      // bloque gagne. Il departage les SUJETS entre eux, jamais un sujet et un
      // fait du compte -- sinon « mon coffre est-il synchronise ? » recevrait
      // un cours sur la synchro au lieu de l'etat reel du coffre.
      return { topic, points, pointsContextuels: points + (topic.views.includes(view) ? 0.5 : 0) };
    })
    .sort((a, b) => b.pointsContextuels - a.pointsContextuels)[0];

  const pointsFait = meilleurFait?.points ?? 0;
  const pointsSujet = meilleurSujet?.points ?? 0;
  if (pointsFait < 1 && pointsSujet < 1) {
    return {
      kind: 'inconnu',
      title: 'Je ne sais pas',
      text:
        'Je ne sais pas repondre a cette question. Je ne reponds que sur ce que je peux citer — la ' +
        `documentation embarquee et l’etat de votre coffre — et je prefere le dire plutot que d’inventer. ${HELP_ESCALATION}`,
      sources: [],
      related: suggestions,
    };
  }

  // Une question sur l'etat du compte l'emporte sur la documentation : elle
  // parle de SA situation, pas du produit en general.
  if (meilleurFait !== undefined && pointsFait >= pointsSujet) {
    const { fait } = meilleurFait;
    const complement =
      meilleurSujet !== undefined && pointsSujet >= 1 ? ` ${meilleurSujet.topic.answer}` : '';
    return {
      kind: 'compte',
      title: fait.label,
      text: `${fait.label} : ${fait.value}.${complement}`,
      sources:
        complement === '' || meilleurSujet === undefined
          ? [ACCOUNT_SOURCE]
          : [ACCOUNT_SOURCE, meilleurSujet.topic.source],
      related: suggestions,
    };
  }

  const { topic } = meilleurSujet!;
  return {
    kind: 'documentation',
    title: topic.question,
    text: topic.answer,
    sources: [topic.source],
    related: helpTopicsForView(view)
      .filter((autre) => autre.id !== topic.id)
      .slice(0, 3)
      .map((autre) => autre.question),
  };
}
